/**
 * PavoPay POS Device Integration
 * PavoPay REST API Entegrasyonu
 * 
 * NOT: PavoPay local-service PROXY kullanır!
 * localStorage ayarları local-service tarafından yönetilir.
 * CORS bypass için local-service proxy üzerinden PavoPay API'sine bağlanır.
 */

import {
  IFiscalDevice,
  FiscalProductItem,
  FiscalOperationResult,
  FiscalApiResponse,
} from './types';
import { getRestP2PConfig } from '../../../../../services/restP2PConfig';
import { restP2PPaymentService } from '../../../../../services/restP2PPaymentService';

// Local-Service API Base URL (varsayılan, setLocalServiceUrl ile değiştirilebilir)
const DEFAULT_LOCAL_SERVICE_URL = 'http://localhost:3003';
let currentLocalServiceUrl = DEFAULT_LOCAL_SERVICE_URL;

// ✅ YENİ: Yerel servis URL'sini ayarla (QuickSaleScreen'den çağrılır)
export const setLocalServiceUrl = (url: string) => {
  if (url && url !== currentLocalServiceUrl) {
    console.log('[PavoPay] 🔌 Yerel servis URL güncellendi:', url);
    currentLocalServiceUrl = url;
  }
};

// Dinamik API base URL'i hesapla
const getLocalServiceApiBaseUrl = () => `${currentLocalServiceUrl}/api/yazarkasa`;
const getDefaultLocalServiceApiBaseUrl = () => `${DEFAULT_LOCAL_SERVICE_URL}/api/yazarkasa`;

const fetchWithFallback = async (url: string, options: RequestInit): Promise<Response> => {
  try {
    const response = await fetch(url, options);
    return response;
  } catch (primaryError) {
    if (currentLocalServiceUrl !== DEFAULT_LOCAL_SERVICE_URL) {
      const fallbackUrl = url.replace(currentLocalServiceUrl, DEFAULT_LOCAL_SERVICE_URL);
      console.warn(`[LocalService] Birincil URL başarısız (${currentLocalServiceUrl}), fallback deneniyor: ${DEFAULT_LOCAL_SERVICE_URL}`);
      return await fetch(fallbackUrl, options);
    }
    throw primaryError;
  }
};

// PavoPay Settings Interface (local-service localStorage'dan gelir)
interface PavoPaySettings {
  posIp: string; // Örn: 192.168.1.100
  port: string; // 4568 (HTTP) veya 4567 (HTTPS)
  serialNumber: string; // POS seri numarası
  fingerprint: string; // Uygulama ID
  sequence: number; // Transaction sequence (her işlemde artırılır)
}

// PavoPay Transaction Handle
interface TransactionHandle {
  SerialNumber: string;
  Fingerprint: string;
  TransactionSequence: number;
  TransactionDate: string; // ISO 8601
}

// PavoPay Sale Request
interface PavoPaySaleRequest {
  TransactionHandle: TransactionHandle;
  Sale: {
    RefererApp: string;
    RefererAppVersion: string;
    OrderNo?: string; // ✅ YENİ: Timeout durumunda satışı sorgulamak için
    MainDocumentType: number; // 1 = E-Fatura, 2 = E-Arşiv
    GrossPrice: number;
    TotalPrice: number;
    SendPhoneNotification: boolean;
    SendEMailNotification: boolean;
    SkipAmountCash: boolean; // true = Nakit ise tutar sormadan direkt kes
    ShowCreditCardMenu: boolean; // false = Satış/Puanlı satış menüsünü gösterme
    AskCustomer: boolean; // false = Müşteri bilgisi sorma
    CustomerParty?: any; // Müşteri bilgisi (cari sorgusundan)
    SelectedSlots: string[]; // ['rf', 'icc', 'manual']
    CardReadTimeout: number; // Saniye
    CancelPaymentLater: boolean;
    TryAgainOnPaymentFailure: boolean;
    AddedSaleItems: {
      Name: string;
      IsGeneric: boolean;
      UnitCode: string; // 'C62' = Adet
      TaxGroupCode: string; // 'KDV20', 'KDV10', 'KDV1', 'KDV0'
      ItemQuantity: number;
      UnitPriceAmount: number;
      GrossPriceAmount: number;
      TotalPriceAmount: number;
    }[];
    PaymentInformations?: {
      Mediator: number; // 1=Nakit, 2=Kredi Kartı, 3=Yemek Kartı
      Amount: number;
      CurrencyCode: string; // 'TRY'
      ExchangeRate: number;
    }[];
    ExternalPayments?: {
      Type: number;      // 3 = Yemek Kartı
      Mediator: number;  // 10 = Harici Yemek Kartı
      Brand: number;     // 6=Multinet, 7=Metropol, 8=Pluxee, 9=Ticket, 10=Setcard, 11=TokenFlex, 12=Paye
      Amount: number;
      CurrencyCode?: string;
      ExchangeRate?: number;
    }[];
    AllowedPaymentMediators?: {
      Mediator: number;
    }[];
    ReceiptInformation?: {
      PrintCustomerReceipt?: boolean;
      PrintCustomerReceiptCopy?: boolean;
      PrintMerchantReceipt?: boolean;
      ReceiptWidth?: string; // '58mm' | '80mm'
      ReceiptImageEnabled?: boolean;
      ReceiptJsonEnabled?: boolean;
      ReceiptTextEnabled?: boolean;
      EnableExchangeRateField?: boolean;
    };
    TopPrintableItems?: any[];
    BottomPrintableItems?: any[];
  };
}

// PavoPay API Response
interface PavoPayResponse {
  HasError: boolean;
  Message?: string;
  ErrorCode?: string;
  Sale?: {
    SaleNumber?: string;
    InvoiceNo?: string;
    Status?: string;
    StatusId?: number;
    RemainingPaymentAmount?: number;
    AddedPayments?: any[];
    FinancialDocuments?: PavoPayFinancialDocument[];
  };
  Data?: {
    SaleNumber?: string;
    InvoiceNo?: string;
    Status?: string;
    StatusId?: number;
    RemainingPaymentAmount?: number;
    AddedPayments?: any[];
    FinancialDocuments?: PavoPayFinancialDocument[];
  };
}

interface PavoPayFinancialDocument {
  Id?: number;
  InvoiceNo?: string;
  DocumentNo?: string; // ETTN (UUID)
  InquiryLink?: string;
  Type?: {
    Id?: number;
    Name?: string; // "E-Fatura", "E-Arşiv", "E-Archive" vb.
    Cancellable?: boolean;
  };
}

/**
 * PavoPay response'undan ETTN (DocumentNo) çıkar
 * FinancialDocuments[0].DocumentNo → ETTN (UUID format)
 */
const extractETTNFromResponse = (saleData: any): string | undefined => {
  const financialDocs = saleData?.FinancialDocuments || saleData?.financialDocuments;
  if (!financialDocs || !Array.isArray(financialDocs) || financialDocs.length === 0) return undefined;
  const ettn = financialDocs[0]?.DocumentNo || financialDocs[0]?.documentNo;
  if (ettn) {
    console.log('[PavoPay] 📋 ETTN bulundu:', ettn, '| Belge Tipi:', financialDocs[0]?.Type?.Name || 'Bilinmiyor');
  }
  return ettn || undefined;
}

// ✅ YENİ: PavoPay Satış Kaydı Interface
export interface PavoPaySaleRecord {
  saleNumber: string;      // "ol3A4iYnre"
  invoiceNo: string;       // "WUG2026000000004"
  amount: number;          // 79.90
  date: string;            // ISO date
  paymentType: 'CASH' | 'POS' | 'KARMA';
  items?: { name: string; quantity: number; price: number }[];
  fiscalData?: any;        // Ham PavoPay fiscal response
  // Kulüp girişi satışlarında bu satışa atanan kart numarası/numaraları (UID).
  // Birden fazla kart varsa virgülle birleştirilir; iade modalinde gösterilir
  // ve arama bu alanı da kapsar.
  cardUID?: string;
  // Rest-P2P fire-and-forget akışında satış kaydı ASENKRON (SignalR/F6) oluşur ve
  // satış no'su atama anında bilinmez. RequestId hem ödeme isteğinde hem F6
  // sonucunda bulunduğundan kart eşleştirmesi için deterministik anahtardır.
  requestId?: string;
}

// PavoPay Pending CustomerParty (satış sırasında kullanılır)
let pendingCustomerParty: any = null;
let pendingIsEInvoice: boolean = false;
let pendingNotificationPhone: string = '';

export const setPendingCustomerParty = (party: any) => {
  pendingCustomerParty = party;
  if (party) {
    console.log('[PavoPay] CustomerParty ayarlandı:', party.FirstName || party.CompanyName);
  } else {
    console.log('[PavoPay] CustomerParty temizlendi');
  }
};

export const getPendingCustomerParty = () => pendingCustomerParty;

export const setPendingIsEInvoice = (value: boolean) => {
  pendingIsEInvoice = value;
  console.log('[PavoPay] Belge tipi ayarlandı:', value ? 'E-Fatura' : 'E-Arşiv');
};

export const getPendingIsEInvoice = () => pendingIsEInvoice;

export const setPendingNotificationPhone = (phone: string) => {
  pendingNotificationPhone = phone;
  if (phone) {
    console.log('[PavoPay] Bildirim telefonu ayarlandı:', phone);
  }
};

export const getPendingNotificationPhone = () => pendingNotificationPhone;

export const clearPendingCustomerParty = () => {
  pendingCustomerParty = null;
  pendingIsEInvoice = false;
  pendingNotificationPhone = '';
};

const getMainDocumentType = (): number => {
  // PavoPay API sadece 1 (E-Fatura) ve 9 (Gider Pusulası) destekler
  // E-Fatura vs E-Arşiv ayrımını PavoPay müşteri kaydına göre otomatik yapar
  return 1;
};

// ═══════════════════════════════════════════════════════════════════════
// Fiş üst/alt bilgi helper — Mutfak entegrasyonu açıkken PavoPay intent'ine
// eklenen TopPrintableItems / BottomPrintableItems'ı merkezi üretir.
// Rest (doğrudan pairing) + Rest-P2P + gts_pavo Flutter uygulaması aynı
// çıktıyı kullanır; görünüm değişirse tek yerden güncellenir.
// ═══════════════════════════════════════════════════════════════════════
export interface KitchenPrintableItems {
  top: any[] | undefined;
  bottom: any[];
}

export const buildKitchenPrintableItems = (
  kitchenOrderCode: string | null | undefined,
  receiptCompanyName?: string | null,
): KitchenPrintableItems => {
  if (!kitchenOrderCode) return { top: undefined, bottom: [] };
  const top: any[] = [
    ...(receiptCompanyName
      ? [
          { type: 'dSpace', height: 20 },
          { type: 'dText', alignment: 'center', fontSize: 32, text: receiptCompanyName, fontWeight: 'bold' },
          { type: 'dSpace', height: 15 },
        ]
      : []),
    { type: 'dSpace', height: receiptCompanyName ? 10 : 30 },
    { type: 'dText', alignment: 'center', fontSize: 48, text: kitchenOrderCode, fontWeight: 'bold' },
    { type: 'dSpace', height: 8 },
    { type: 'dText', alignment: 'center', fontSize: 20, text: 'Siparişinizi Ekrandan', fontWeight: 'normal' },
    { type: 'dText', alignment: 'center', fontSize: 20, text: 'Takip Edebilirsiniz.', fontWeight: 'normal' },
    { type: 'dSpace', height: 10 },
    { type: 'dLine', dashWidth: 4 },
    { type: 'dSpace', height: 15 },
  ];
  const bottom: any[] = [
    { type: 'dSpace', height: 20 },
    { type: 'dLine', dashWidth: 4 },
    { type: 'dSpace', height: 15 },
    { type: 'dText', alignment: 'center', fontSize: 36, text: 'AQUASOFT', fontWeight: 'bold' },
    { type: 'dSpace', height: 20 },
  ];
  return { top, bottom };
};

const buildSaleCustomerParty = (party: any): any => {
  if (!party) return null;
  return {
    CustomerType: party.CustomerType || 1,
    FirstName: party.FirstName || '',
    MiddleName: '',
    FamilyName: party.FamilyName || '',
    CompanyName: party.CompanyName || '',
    TaxOfficeCode: party.TaxOfficeCode || '',
    TaxNumber: party.TaxNumber || '',
    Phone: party.Phone || '',
    EMail: party.EMail || '',
    Country: party.Country || 'Türkiye',
  };
};

// ✅ PavoPay Müşteri Sorgu Tipleri
export interface PavoPayCustomerParty {
  CustomerType: number; // 1 = Bireysel, 2 = Kurumsal
  CompanyName?: string;
  FirstName?: string;
  FamilyName?: string;
  TaxNumber?: string;
  TaxOfficeName?: string;
  TaxOfficeCode?: string;
  Address?: string;
  Phone?: string;
  EMail?: string;
  Country?: string;
  City?: string;
  District?: string;
}

export interface PavoPayCustomerQueryResult {
  success: boolean;
  errorMessage?: string;
  customerParty?: PavoPayCustomerParty;
  rawInfo?: {
    faalTerkDurumu?: string;
    sirketinTuru?: string;
    iseBaslamaTarihi?: string;
    isiBirakmaTarihi?: string;
    vkn?: string;
    tckn?: string;
    unvan?: string;
    kimlikUnvani?: string;
    [key: string]: any;
  };
}

// ✅ YENİ: PavoPay satışlarını localStorage'a kaydet
const PAVOPAY_SALES_KEY_PREFIX = 'pavopaySales_';

// ✅ LocalStorage optimizasyon ayarları
const MAX_SALES_COUNT = 50; // Maksimum satış sayısı (100'den 50'ye düşürüldü)
const MAX_SALE_AGE_DAYS = 30; // 30 günden eski satışları sil
const MAX_STORAGE_SIZE_KB = 500; // Maksimum ~500 KB (5 MB limitin %10'u)

/**
 * FiscalData'dan sadece gerekli alanları çıkar (boyut optimizasyonu)
 * Tam response çok büyük olabilir, sadece iade için gerekli alanları tut
 */
const sanitizeFiscalData = (fiscalData: any): any => {
  if (!fiscalData) return undefined;
  
  // Sadece gerekli alanları tut
  const data = fiscalData.Data || fiscalData.data || fiscalData;
  return {
    SaleNumber: data.SaleNumber || data.saleNumber,
    InvoiceNo: data.InvoiceNo || data.invoiceNo || 
               data.FinancialDocuments?.[0]?.InvoiceNo,
    ETTN: extractETTNFromResponse(data),
    StatusId: data.StatusId || data.statusId,
    Status: data.Status || data.status,
    SaleDate: data.SaleDate || data.saleDate,
    TotalPrice: data.TotalPrice || data.totalPrice,
  };
};

/**
 * Eski satışları temizle (30 günden eski)
 */
const cleanOldSales = (sales: PavoPaySaleRecord[]): PavoPaySaleRecord[] => {
  const now = new Date().getTime();
  const maxAge = MAX_SALE_AGE_DAYS * 24 * 60 * 60 * 1000; // 30 gün in ms
  
  const cleaned = sales.filter(sale => {
    try {
      const saleDate = new Date(sale.date).getTime();
      return (now - saleDate) < maxAge;
    } catch {
      return true; // Parse hatası varsa satışı tut
    }
  });
  
  if (cleaned.length < sales.length) {
    console.log(`[PavoPay] 🧹 ${sales.length - cleaned.length} eski satış temizlendi (${MAX_SALE_AGE_DAYS} günden eski)`);
  }
  
  return cleaned;
};

export const savePavoPaySale = (sale: PavoPaySaleRecord, companyId: string): void => {
  try {
    console.log('[PavoPay] 💾 savePavoPaySale çağrıldı:', { saleNumber: sale.saleNumber, companyId });
    const key = `${PAVOPAY_SALES_KEY_PREFIX}${companyId}`;
    
    const existing = localStorage.getItem(key);
    let sales: PavoPaySaleRecord[] = existing ? JSON.parse(existing) : [];
    console.log('[PavoPay] 💾 Mevcut satış sayısı:', sales.length);
    
    // Aynı saleNumber varsa ekleme (duplicate prevention)
    if (sales.some(s => s.saleNumber === sale.saleNumber)) {
      console.log('[PavoPay] ⚠️ Satış zaten kayıtlı:', sale.saleNumber);
      return;
    }
    
    // ✅ FiscalData'yı hafiflet (boyut optimizasyonu)
    const optimizedSale: PavoPaySaleRecord = {
      ...sale,
      fiscalData: sanitizeFiscalData(sale.fiscalData),
    };

    // Kulüp girişi: kart ataması bu satıştan ÖNCE yapılmış (requestId ile bekliyor)
    // olabilir → kaydı oluştururken kart UID'ini iliştir.
    if (!optimizedSale.cardUID) {
      const pendingCard = consumePendingCardForRequestId(optimizedSale.requestId);
      if (pendingCard) {
        optimizedSale.cardUID = pendingCard;
        console.log('[PavoPay] ✅ Bekleyen kart kayda iliştirildi:', optimizedSale.requestId, '→', pendingCard);
      }
    }
    
    // ✅ Eski satışları temizle (30 günden eski)
    sales = cleanOldSales(sales);
    
    sales.unshift(optimizedSale); // En yenisi başa ekle
    
    // ✅ Maksimum satış sayısı kontrolü (50 adet)
    if (sales.length > MAX_SALES_COUNT) {
      const removed = sales.length - MAX_SALES_COUNT;
      sales.splice(MAX_SALES_COUNT);
      console.log(`[PavoPay] 🧹 ${removed} eski satış silindi (limit: ${MAX_SALES_COUNT})`);
    }
    
    // ✅ Boyut kontrolü (tahmini)
    const dataString = JSON.stringify(sales);
    const sizeKB = Math.round(dataString.length / 1024);
    
    if (sizeKB > MAX_STORAGE_SIZE_KB) {
      console.warn(`[PavoPay] ⚠️ LocalStorage boyutu yüksek: ${sizeKB} KB (limit: ${MAX_STORAGE_SIZE_KB} KB)`);
      // Boyut aşıldıysa eski satışları daha agresif sil
      while (sales.length > 10 && JSON.stringify(sales).length / 1024 > MAX_STORAGE_SIZE_KB) {
        sales.pop();
      }
      console.log(`[PavoPay] 🧹 Boyut optimizasyonu sonrası satış sayısı: ${sales.length}`);
    }
    
    localStorage.setItem(key, JSON.stringify(sales));
    console.log(`[PavoPay] ✅ Satış kaydedildi: ${sale.saleNumber} (Toplam: ${sales.length}, ~${sizeKB} KB)`);
  } catch (error) {
    console.error('[PavoPay] Satış kaydetme hatası:', error);
  }
};

// ✅ YENİ: PavoPay satışlarını getir
export const getPavoPaySales = (companyId: string): PavoPaySaleRecord[] => {
  try {
    const key = `${PAVOPAY_SALES_KEY_PREFIX}${companyId}`;
    console.log('[PavoPay] 📂 Satışlar getiriliyor. Key:', key);
    const existing = localStorage.getItem(key);
    let sales: PavoPaySaleRecord[] = existing ? JSON.parse(existing) : [];
    
    // ✅ Her çağrıda eski satışları temizle
    const originalCount = sales.length;
    sales = cleanOldSales(sales);
    
    // Temizleme olduysa güncelle
    if (sales.length < originalCount) {
      localStorage.setItem(key, JSON.stringify(sales));
    }
    
    console.log('[PavoPay] 📂 Bulunan satış sayısı:', sales.length);
    return sales;
  } catch (error) {
    console.error('[PavoPay] Satış getirme hatası:', error);
    return [];
  }
};

// ✅ YENİ: PavoPay satış kaydını sil (iade sonrası)
export const removePavoPaySale = (saleNumber: string, companyId: string): void => {
  try {
    const key = `${PAVOPAY_SALES_KEY_PREFIX}${companyId}`;
    const existing = localStorage.getItem(key);
    if (!existing) return;
    
    const sales: PavoPaySaleRecord[] = JSON.parse(existing);
    const filtered = sales.filter(s => s.saleNumber !== saleNumber);
    
    localStorage.setItem(key, JSON.stringify(filtered));
    console.log('[PavoPay] ✅ Satış kaydı silindi:', saleNumber);
  } catch (error) {
    console.error('[PavoPay] Satış silme hatası:', error);
  }
};

// ✅ Kulüp girişi: atanan kart numarasını (UID) mevcut satış kaydına iliştir.
// Satış kaydı (POS/Nakit) ödeme sırasında oluşturulur; kart ataması ise ödemeden
// SONRA yapıldığı için kart bilgisi burada güncellenir. Kayıt bulunamazsa no-op.
export const updatePavoPaySaleCardInfo = (
  saleNumber: string | undefined | null,
  companyId: string,
  cardUID: string | undefined | null
): boolean => {
  if (!saleNumber || !companyId || !cardUID) return false;
  try {
    const key = `${PAVOPAY_SALES_KEY_PREFIX}${companyId}`;
    const existing = localStorage.getItem(key);
    if (!existing) return false;

    const sales: PavoPaySaleRecord[] = JSON.parse(existing);
    let updated = false;
    const next = sales.map(s => {
      if (s.saleNumber === saleNumber) {
        updated = true;
        return { ...s, cardUID };
      }
      return s;
    });

    if (updated) {
      localStorage.setItem(key, JSON.stringify(next));
      console.log('[PavoPay] ✅ Satış kaydına kart bilgisi eklendi:', saleNumber, '→', cardUID);
    } else {
      console.log('[PavoPay] ℹ️ Kart bilgisi iliştirilecek satış kaydı bulunamadı:', saleNumber);
    }
    return updated;
  } catch (error) {
    console.error('[PavoPay] Kart bilgisi güncelleme hatası:', error);
    return false;
  }
};

// ─────────────────────────────────────────────────────────────────────────
// Rest-P2P fire-and-forget NAKİT akışında kulüp giriş kart eşleştirmesi.
// Satış kaydı F6 (SignalR) dinleyicisi tarafından ASENKRON oluşturulur; kart
// ataması ise ödemeden sonra ayrı bir adımda yapılır. İkisi arasındaki tek
// deterministik bağ `RequestId`'dir. Tek terminalde işlemler sıralı olduğundan
// son fire-and-forget requestId'sini modülde tutar, atama anında okuruz.
// ─────────────────────────────────────────────────────────────────────────
let lastClubEntryFireForgetRequestId: string | null = null;
// Kayıt henüz oluşmadıysa: requestId → kart UID (kayıt oluşurken iliştirilir).
const pendingCardByRequestId = new Map<string, { cardUID: string; ts: number }>();
const PENDING_CARD_TTL_MS = 5 * 60 * 1000;

export const setLastClubEntryFireForgetRequestId = (requestId: string | null): void => {
  lastClubEntryFireForgetRequestId = requestId || null;
};

export const getLastClubEntryFireForgetRequestId = (): string | null => lastClubEntryFireForgetRequestId;

const prunePendingCardByRequestId = (): void => {
  const now = Date.now();
  for (const [rid, v] of pendingCardByRequestId) {
    if (now - v.ts > PENDING_CARD_TTL_MS) pendingCardByRequestId.delete(rid);
  }
};

// savePavoPaySale içinden çağrılır: yeni kaydın requestId'sine ait bekleyen kart
// varsa iliştirir. Kart UID döner (yoksa undefined).
const consumePendingCardForRequestId = (requestId: string | undefined): string | undefined => {
  if (!requestId) return undefined;
  prunePendingCardByRequestId();
  const entry = pendingCardByRequestId.get(requestId);
  if (!entry) return undefined;
  pendingCardByRequestId.delete(requestId);
  return entry.cardUID;
};

// RequestId'ye göre kart iliştir. Kayıt bulunduysa günceller; bulunamadıysa
// (henüz oluşmadıysa) bekleyen listeye koyar, kayıt oluşurken iliştirilir.
export const linkClubEntryCardByRequestId = (
  companyId: string,
  requestId: string | undefined | null,
  cardUID: string | undefined | null
): void => {
  if (!companyId || !requestId || !cardUID) return;
  try {
    const key = `${PAVOPAY_SALES_KEY_PREFIX}${companyId}`;
    const existing = localStorage.getItem(key);
    const sales: PavoPaySaleRecord[] = existing ? JSON.parse(existing) : [];
    let updated = false;
    const next = sales.map(s => {
      if (s.requestId === requestId) {
        updated = true;
        return { ...s, cardUID };
      }
      return s;
    });
    if (updated) {
      localStorage.setItem(key, JSON.stringify(next));
      console.log('[PavoPay] ✅ Kart bilgisi requestId ile satışa eklendi:', requestId, '→', cardUID);
    } else {
      prunePendingCardByRequestId();
      pendingCardByRequestId.set(requestId, { cardUID, ts: Date.now() });
      console.log('[PavoPay] ⏳ Satış henüz oluşmadı, kart requestId için bekletiliyor:', requestId, '→', cardUID);
    }
  } catch (error) {
    console.error('[PavoPay] requestId kart eşleştirme hatası:', error);
  }
};

// Tek giriş noktası: kulüp giriş ödemesi sonrası karta UID iliştir.
//  • Satış no biliniyorsa (POS / doğrudan PavoPay nakit) → kesin eşleştirme.
//  • Bilinmiyorsa (Rest-P2P fire-and-forget nakit) → son fire-and-forget
//    requestId üzerinden eşleştir.
export const linkClubEntryCard = (
  companyId: string | undefined | null,
  cardUID: string | undefined | null,
  opts?: { pavoPaySaleNumber?: string | null }
): void => {
  if (!companyId || !cardUID) return;
  if (opts?.pavoPaySaleNumber) {
    const ok = updatePavoPaySaleCardInfo(opts.pavoPaySaleNumber, companyId, cardUID);
    if (ok) return; // kesin eşleşti
  }
  // Satış no yok ya da kayıt bulunamadı → requestId yolunu dene.
  const rid = getLastClubEntryFireForgetRequestId();
  if (rid) {
    linkClubEntryCardByRequestId(companyId, rid, cardUID);
  }
};

// ✅ Backend POS iade (muhasebe) kaydını oluştur — geçici hatalara karşı tekrar dener.
// Sistemde manuel iade girişi olmadığından bu kaydın oluşması KRİTİK.
const tryCreatePosRefundRecord = async (
  saleNumber: string,
  refundAmount: number,
  createdBy: string | undefined,
  description: string,
  logPrefix: string,
): Promise<boolean> => {
  const { default: salesService } = await import('../../../../../services/salesService');
  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      const refundResult = await salesService.processPosRefund({
        PavoPaySaleNumber: saleNumber,
        RefundAmount: refundAmount,
        CreatedBy: createdBy || 'system',
        Description: description,
      });
      if (refundResult.Success === true) {
        console.log(
          `${logPrefix} ✅ Backend iade işlendi (deneme ${attempt}/3):`,
          refundResult.Message || '',
          refundResult.DeletedInvoiceNumbers?.length
            ? `Silinen fatura(lar): ${refundResult.DeletedInvoiceNumbers.join(', ')}`
            : '',
        );
        return true;
      }
      console.warn(`${logPrefix} ⚠️ Backend iade kaydı başarısız (deneme ${attempt}/3):`, refundResult);
    } catch (err) {
      console.error(`${logPrefix} ❌ Backend iade kaydı hatası (deneme ${attempt}/3):`, err);
    }
    if (attempt < 3) await new Promise(r => setTimeout(r, 600));
  }
  console.error(`${logPrefix} ⛔ Backend iade kaydı 3 denemede de OLUŞTURULAMADI: ${saleNumber}`);
  return false;
};

// ✅ YENİ: PavoPay satış iptal et
export const cancelPavoPaySale = async (
  saleNumber: string,
  companyId: string,
  refundAmount?: number,
  createdBy?: string
): Promise<{ success: boolean; errorMessage?: string; backendRefundSuccess?: boolean }> => {
  // ✅ F5: Rest-P2P aktifse yerel servis yerine Pavo cihazina SignalR ile yolla
  const restCfg = getRestP2PConfig();
  if (restCfg.enabled && restCfg.deviceCode) {
    try {
      console.log('[PavoPay→RestP2P] 🔄 Rest-P2P cancelSale:', saleNumber);
      // ⬇️ PAVO İSPAT: Cihaza gönderilen işlemin CancelSale (Satış İptali / VOID)
      //    olduğunu Chrome console'da açıkça kanıtlamak için ayrıntılı log.
      console.log(
        '%c[PAVO İSPAT] CİHAZA GÖNDERİLEN İŞLEM = CancelSale (SATIŞ İPTALİ / VOID) — İADE (PartialReturn) DEĞİL',
        'background:#b71c1c;color:#fff;font-weight:bold;padding:2px 6px;border-radius:3px;',
        {
          yontem: 'Rest-P2P (SignalR → cihaz)',
          pavoIntent: 'pavopay.intent.action.cancel.sale',
          backendEndpoint: '/api/PavoPayment/refund/cancel-sale',
          gonderilenPayload: {
            DeviceCode: restCfg.deviceCode,
            SaleNumber: saleNumber,
            Amount: refundAmount,
            IsVoid: true,
          },
          aciklama: 'Bu çağrı orijinal satışın ÖDEMESİNİ İPTAL (void) eder; Sale.IsVoid=true gönderilir; yeni bir iade (PartialReturn/mediator iade) işlemi DEĞİLDİR.',
        },
      );
      const outcome = await restP2PPaymentService.requestCancelSale({
        deviceCode: restCfg.deviceCode,
        saleNumber,
        amount: refundAmount,
        isVoid: true,
        companyId: restCfg.companyId ?? companyId,
        branchId: restCfg.branchId,
      });

      if (outcome.status !== 'completed') {
        const errMsg = outcome.message || 'Rest-P2P iptal başarısız';
        const errCode = (outcome as any).errorCode || 'UNKNOWN';
        console.error(
          `[PavoPay→RestP2P] ❌ cancelSale BAŞARISIZ - saleNumber=${saleNumber}, ` +
          `errorCode=${errCode}, message=${errMsg}`,
        );
        if (errCode === 'ENDPOINT_NOT_FOUND') {
          console.error(
            '[PavoPay→RestP2P] ⚠️ Backend gts_pavo_api henüz yeni refund endpoint\'leri ile deploy edilmemiş olabilir. ' +
            'Lütfen prod API\'yi yeniden publish edin ve SQL migration\'ı çalıştırın.',
          );
        }
        return { success: false, errorMessage: errMsg };
      }

      // ⚠️ KRİTİK: Rest-P2P cihaz bridge'i "completed" dönse de gerçek void
      // durumunu iletmiyor (sadece {SaleNumber}). Kasiyer cihazda "Satıştan
      // Vazgeç" derse de completed geliyor → para iade EDİLMEMİŞ olsa bile.
      // Bu yüzden iptal sonrası satışı getReturnableSale ile tekrar sorgulayıp
      // gerçekten void edilmiş mi DOĞRULUYORUZ.
      //
      // NOT: getSaleResult (reconciliation) yolu denendi ancak cihazdaki companion
      // app reconciliation isteğine cevap vermediği için her seferinde timeout'a
      // düşüp 25sn boşa bekletiyordu. getReturnableSale anında ve doğru cevap
      // verdiği için doğrudan onu kullanıyoruz:
      //   - success=true & kalan void edilebilir tutar > 0  → VAZGEÇİLDİ (engelle)
      //   - success=false & "DocumentFailed / not eligible" → zaten void = BAŞARILI
      //   - success=true & kalan tutar yok                  → void = BAŞARILI
      //
      // voidConfirmed: true = iptal edildi, false = vazgeçildi, null = belirsiz.
      let voidConfirmed: boolean | null = null;

      try {
        const verify = await getReturnableSale(0, saleNumber);
        const totalRemainingVoidable = (verify.payments || [])
          .reduce((sum: number, p: any) => sum + (p?.remainingVoidableAmount ?? 0), 0);
        const verifyErrMsg = (verify.errorMessage || '').toLowerCase();
        console.log(
          `[PavoPay→RestP2P][VOID-VERIFY/getReturnableSale] saleNumber=${saleNumber} → success=${verify.success}, ` +
          `grossPrice=${verify.grossPrice}, kalanVoidEdilebilir=${totalRemainingVoidable}, err=${verify.errorMessage || '-'}`,
        );
        if (verify.success) {
          // Satış hâlâ void edilebilir → vazgeçildi; tutar kalmadıysa void edildi.
          voidConfirmed = totalRemainingVoidable > 0.01 ? false : true;
        } else {
          // "iade edilemez / iptal edilmiş / DocumentFailed" = satış zaten ölü = void BAŞARILI.
          const voidConfirmedByError =
            verifyErrMsg.includes('cannot be returned') ||
            verifyErrMsg.includes('not eligible') ||
            verifyErrMsg.includes('documentfailed') ||
            verifyErrMsg.includes('cancel') ||
            verifyErrMsg.includes('iptal') ||
            verifyErrMsg.includes('iade edil');
          voidConfirmed = voidConfirmedByError ? true : null;
        }
      } catch (verifyErr) {
        console.warn('[PavoPay→RestP2P][VOID-VERIFY] getReturnableSale sorgusu hata verdi, doğrulama atlandı:', verifyErr);
        voidConfirmed = null;
      }

      // ── Karar ──────────────────────────────────────────────────────────────
      if (voidConfirmed === false) {
        console.error(
          `[PavoPay→RestP2P] ⛔ İPTAL GERÇEKLEŞMEDİ - saleNumber=${saleNumber} ` +
          `(satıştan vazgeçilmiş). Kart TESLİM ALINMAYACAK, iade kaydı oluşturulmayacak.`,
        );
        return {
          success: false,
          errorMessage: 'Pavo satış iptali gerçekleşmedi (cihazda vazgeçildi) — para iade edilmedi, kart teslim alınmadı',
        };
      }
      if (voidConfirmed === null) {
        // Belirsiz: engellemek genuine iptalleri bozabilir → devam ama uyar.
        console.warn(
          `[PavoPay→RestP2P][VOID-VERIFY] ⚠️ Doğrulama belirsiz (saleNumber=${saleNumber}), ` +
          `iptalin gerçekleştiği varsayılarak devam ediliyor.`,
        );
      } else {
        console.log(`[PavoPay→RestP2P][VOID-VERIFY] ✅ Satış iptali doğrulandı (saleNumber=${saleNumber}).`);
      }

      // Pavo'da basarili → backend iade kaydi (mevcut tutar varsa)
      removePavoPaySale(saleNumber, companyId);
      let backendRefundSuccess = false;
      if (refundAmount && refundAmount > 0) {
        backendRefundSuccess = await tryCreatePosRefundRecord(
          saleNumber,
          refundAmount,
          createdBy,
          `PavoPay (Rest-P2P) POS İade - ${saleNumber}`,
          '[PavoPay→RestP2P]',
        );
      }
      return { success: true, backendRefundSuccess };
    } catch (err: any) {
      console.error('[PavoPay→RestP2P] cancelSale hatasi:', err);
      return { success: false, errorMessage: err?.message || 'Rest-P2P iptal hatası' };
    }
  }

  try {
    console.log('[PavoPay] 🔄 Satış iptal ediliyor:', saleNumber);

    // ✅ Farklı cihazdan iptal desteği: satışın bankasını bulup BU cihazdaki
    // aynı bankanın terminalId'sini SelectedTerminals olarak ekle. Pavo iptali
    // doğru bankaya yönlendirir (aksi halde cihaz varsayılan bankaya atabiliyor).
    // Çözülemezse null döner ve iptal eski davranışla devam eder.
    const selectedTerminals = await resolveCancelSelectedTerminals(saleNumber);

    const settings = await getPavoPaySettings();
    if (!settings) {
      return { success: false, errorMessage: 'PavoPay ayarları bulunamadı' };
    }
    
    const handle = createTransactionHandle(settings);
    
    const cancelRequest = {
      TransactionHandle: handle,
      Sale: {
        SaleNumber: saleNumber,
        // Pavo `Sale.IsVoid`: void (anlık iptal) işlemi olduğunu açıkça belirt.
        IsVoid: true,
        ...(selectedTerminals ? { SelectedTerminals: selectedTerminals } : {})
      }
    };
    
    console.log('[PavoPay] CancelSale request:', cancelRequest);
    // ⬇️ PAVO İSPAT: Yerel servis üzerinden cihaza gönderilen işlemin CancelSale
    //    (Satış İptali / VOID) olduğunu Chrome console'da açıkça kanıtlar.
    console.log(
      '%c[PAVO İSPAT] CİHAZA GÖNDERİLEN İŞLEM = CancelSale (SATIŞ İPTALİ / VOID) — İADE (PartialReturn) DEĞİL',
      'background:#b71c1c;color:#fff;font-weight:bold;padding:2px 6px;border-radius:3px;',
      {
        yontem: 'Yerel servis (PavoPay REST)',
        pavoEndpoint: 'POST /CancelSale',
        localServiceEndpoint: `${getLocalServiceApiBaseUrl()}/pavopay-cancel-sale`,
        gonderilenPayload: cancelRequest,
        aciklama: 'Bu çağrı orijinal satışın ÖDEMESİNİ İPTAL (void) eder; yeni bir iade (PartialReturn/mediator iade) işlemi DEĞİLDİR.',
      },
    );
    
    const response = await fetchWithFallback(`${getLocalServiceApiBaseUrl()}/pavopay-cancel-sale`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        posIp: settings.posIp,
        port: settings.port,
        cancelRequest
      }),
    });
    
    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Response okunamadı');
      console.error('[PavoPay] CancelSale HTTP hatası:', response.status, errorText);
      return { 
        success: false, 
        errorMessage: `HTTP ${response.status}: ${response.statusText}` 
      };
    }
    
    const result = await response.json();
    console.log('[PavoPay] CancelSale response:', result);
    
    const isSuccess = result.success && (!result.data?.HasError);
    
    if (isSuccess) {
      removePavoPaySale(saleNumber, companyId);
      console.log('[PavoPay] ✅ Satış iptal edildi:', saleNumber);

      let backendRefundSuccess = false;
      if (refundAmount && refundAmount > 0) {
        backendRefundSuccess = await tryCreatePosRefundRecord(
          saleNumber,
          refundAmount,
          createdBy,
          `PavoPay POS İade - ${saleNumber}`,
          '[PavoPay]',
        );
      }

      return { success: true, backendRefundSuccess };
    } else {
      const errorMessage = result.data?.Message || result.message || 'Satış iptal edilemedi';
      console.error('[PavoPay] ❌ Satış iptal hatası:', errorMessage);
      return { success: false, errorMessage };
    }
    
  } catch (error: any) {
    console.error('[PavoPay] CancelSale genel hatası:', error);
    return { 
      success: false, 
      errorMessage: error.message || 'Bağlantı hatası' 
    };
  }
};

// ✅ YENİ: Sequence cache (bellekte tutuluyor, her seferinde local-service'den okunmuyor)
let cachedSettings: PavoPaySettings | null = null;
let lastSettingsFetchTime: number = 0;
const SETTINGS_CACHE_DURATION = 60000; // 60 saniye

// ✅ YENİ: Local-service API üzerinden PavoPay ayarlarını al (Electron dosya sistemi)
const getPavoPaySettings = async (forceRefresh: boolean = false): Promise<PavoPaySettings | null> => {
  try {
    // ✅ Cache kontrolü - sequence dışındaki ayarları 60 saniye cache'le
    const now = Date.now();
    if (!forceRefresh && cachedSettings && (now - lastSettingsFetchTime) < SETTINGS_CACHE_DURATION) {
      console.log('[PavoPay] 📦 Ayarlar cache\'den alındı (sequence korunuyor):', {
        posIp: cachedSettings.posIp,
        sequence: cachedSettings.sequence
      });
      return cachedSettings;
    }
    
    console.log('[PavoPay] 🌐 Ayarlar local-service API\'sinden isteniyor...');
    
    const response = await fetchWithFallback(`${getLocalServiceApiBaseUrl()}/pavopay-get-settings`, { method: 'GET' });
    
    if (!response.ok) {
      console.error('[PavoPay] ❌ Local-service API hatası:', response.status, response.statusText);
      return cachedSettings; // Hata durumunda mevcut cache'i döndür
    }
    
    const result = await response.json();
    console.log('[PavoPay] 📡 API yanıtı:', result);
    
    if (!result.success || !result.settings) {
      console.warn('[PavoPay] ⚠️ Ayarlar bulunamadı:', result.message);
      return cachedSettings; // Hata durumunda mevcut cache'i döndür
    }
    
    const settings = result.settings as PavoPaySettings;
    
    // Zorunlu alanları kontrol et
    if (!settings.posIp || !settings.serialNumber || !settings.fingerprint) {
      console.error('[PavoPay] ❌ Eksik ayarlar:', settings);
      return cachedSettings;
    }

    // ✅ Sequence yoksa default 1 ver
    if (!settings.sequence || typeof settings.sequence !== 'number') {
      console.warn('[PavoPay] ⚠️ Sequence bulunamadı veya geçersiz, default 1 atanıyor');
      settings.sequence = 1;
    }
    
    // ✅ ÖNEMLI: Eğer cache'de daha yüksek bir sequence varsa, onu koru!
    // (Çünkü bellek güncellemesi local-service'e ulaşmamış olabilir)
    if (cachedSettings && cachedSettings.sequence > settings.sequence) {
      console.log(`[PavoPay] ⚠️ Cache'deki sequence (${cachedSettings.sequence}) daha yüksek, local-service güncellemesi beklemede. Cache korunuyor.`);
      settings.sequence = cachedSettings.sequence;
    }

    console.log('[PavoPay] ✅ Ayarlar başarıyla alındı:', {
      posIp: settings.posIp,
      port: settings.port,
      serialNumber: settings.serialNumber,
      sequence: settings.sequence
    });

    // Cache'i güncelle
    cachedSettings = settings;
    lastSettingsFetchTime = now;

    return settings;
  } catch (error: any) {
    console.error('[PavoPay] ❌ Ayar okuma hatası:', error);
    return cachedSettings; // Hata durumunda mevcut cache'i döndür
  }
};

// ✅ YENİ: Local-service API üzerinden sequence güncelle
const updateSequence = async (newSequence: number): Promise<void> => {
  try {
    console.log('[PavoPay] 🔄 Sequence güncelleniyor:', newSequence);
    
    const response = await fetchWithFallback(`${getLocalServiceApiBaseUrl()}/pavopay-update-sequence`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sequence: newSequence })
    });
    
    const result = await response.json();
    
    if (result.success) {
      console.log('[PavoPay] ✅ Sequence güncellendi:', newSequence);
    } else {
      console.error('[PavoPay] ❌ Sequence güncelleme hatası:', result.message);
    }
  } catch (error) {
    console.error('[PavoPay] ❌ Sequence güncellenemedi:', error);
  }
};

// Transaction Handle oluştur ve sequence'i artır (SENKRON)
const createTransactionHandle = (settings: PavoPaySettings): TransactionHandle => {
  const currentSequence = settings.sequence;
  
  const handle: TransactionHandle = {
    SerialNumber: settings.serialNumber,
    Fingerprint: settings.fingerprint,
    TransactionSequence: currentSequence, // MEVCUT sequence kullan
    TransactionDate: new Date().toISOString()
  };
  
  // ✅ Sequence'i artır (hem cache'de hem local-service'de)
  const newSequence = currentSequence + 1;
  settings.sequence = newSequence; // Bellekte hemen güncelle
  
  // ✅ Cache'i de güncelle (global değişken)
  if (cachedSettings) {
    cachedSettings.sequence = newSequence;
  }
  
  // Arka planda local-service'e de kaydet
  updateSequence(newSequence).catch(err => {
    console.error('[PavoPay] ⚠️ Sequence local-service güncellemesi başarısız (cache korundu):', err);
  });
  
  console.log(`[PavoPay] 🔢 Sequence: ${currentSequence} → ${newSequence} (Transaction için ${currentSequence} kullanıldı)`);
  
  return handle;
};

// PavoPay API çağrısı (Local-Service Proxy üzerinden)
const callPavoPayAPI = async (
  endpoint: string, // 'Pairing' veya 'CompleteSale'
  body: any,
  showAlertModal?: (title: string, message: string) => void
): Promise<FiscalApiResponse> => {
  try {
    const settings = await getPavoPaySettings(); // ✅ await eklendi
    
    if (!settings) {
      // ❌ showAlertModal burada çağrılmıyor - usePayment.ts kendi modalını açacak
      return { 
        success: false, 
        data: { Message: 'PavoPay ayarları bulunamadı. Lütfen local-service yazarkasa kurulum sayfasından ayarları yapın.' }, 
        rawResponse: null 
      };
    }
    
    const endpointMap: Record<string, string> = {
      'Pairing': 'pavopay-pairing',
      'AdvanceSale': 'pavopay-advance-sale',
      'CompleteSale': 'pavopay-complete-sale',
      'GetReturnableSale': 'pavopay-get-returnable-sale',
      'PartialReturnForAdvanceSale': 'pavopay-partial-return-advance',
      'PartialReturn': 'pavopay-partial-return',
      'CancelSale': 'pavopay-cancel-sale',
      'StartSaleWithItems': 'pavopay-start-sale',
      'AddPayment': 'pavopay-add-payment',
      'FinalizeSale': 'pavopay-finalize-sale',
      'AddPaymentAndFinalizeSale': 'pavopay-add-payment-finalize',
      'AbandonSuspendedSale': 'pavopay-abandon-sale',
      'QueryCustomerInfo': 'pavopay-query-customer',
      'GetDeviceInfo': 'pavopay-get-device-info',
    };
    const proxyRoute = endpointMap[endpoint] || 'pavopay-complete-sale';
    const proxyEndpoint = `${getLocalServiceApiBaseUrl()}/${proxyRoute}`;
    
    const proxyBody = {
      posIp: settings.posIp,
      port: settings.port,
      ...(endpoint === 'Pairing' 
        ? { serialNumber: settings.serialNumber, fingerprint: settings.fingerprint }
        : { saleRequest: body })
    };
    
    console.log(`[PavoPay API] POST ${proxyEndpoint} (via local-service)`, proxyBody);
    
    const response = await fetchWithFallback(proxyEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(proxyBody)
    });
    
    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Response okunamadı');
      console.error(`[PavoPay API] HTTP ${response.status}:`, errorText);
      // ❌ showAlertModal burada çağrılmıyor - usePayment.ts kendi modalını açacak
      return { 
        success: false, 
        data: { Message: `Local-service bağlantı hatası (HTTP ${response.status})` }, 
        rawResponse: response 
      };
    }
    
    const proxyResponse = await response.json();
    console.log(`[PavoPay API] Proxy Response:`, proxyResponse);
    
    // Local-service proxy response'u kontrol et
    if (!proxyResponse.success) {
      console.error(`[PavoPay API] Proxy hatası:`, proxyResponse);
      // ❌ showAlertModal burada çağrılmıyor - usePayment.ts kendi modalını açacak
      return { 
        success: false, 
        data: { 
          Message: proxyResponse.message || 'PavoPay işlem hatası',
          ...(proxyResponse.data || {})
        }, 
        rawResponse: response 
      };
    }
    
    // PavoPay API data'sını döndür
    const data: PavoPayResponse = proxyResponse.data;
    
    // ✅ FARKLI ENDPOİNTLER İÇİN FARKLI KONTROLLER
    
    // 1. Pairing (Bağlantı Kontrolü) - Sadece HasError kontrolü yeterli
    if (endpoint === 'Pairing') {
      const hasError = data.HasError === true;
      
      if (hasError) {
        console.error(`[PavoPay API] ❌ Pairing hatası:`, data.Message || 'Bilinmeyen hata', data);
        // ❌ showAlertModal burada çağrılmıyor - usePayment.ts kendi modalını açacak
        return { 
          success: false, 
          data: { ...data, Message: `PavoPay bağlantı hatası: ${data.Message || 'Bilinmeyen hata'}` }, 
          rawResponse: response 
        };
      }
      
      console.log(`[PavoPay API] ✅ Pairing başarılı`);
      return { success: true, data, rawResponse: response };
    }
    
    // 2. AdvanceSale - Detaylı ödeme kontrolü (StatusId + AddedPayments)
    if (endpoint === 'AdvanceSale') {
      const hasError = data.HasError === true;
      const saleData: any = data.Sale || data.Data || {};
      const saleNumber = saleData.SaleNumber || saleData.saleNumber || '';
      const errorMessage = data.Message || data.ErrorCode || '';
      const statusId = saleData.StatusId || saleData.statusId;
      const addedPayments: any[] = saleData.AddedPayments || saleData.addedPayments || [];
      const remainingPayment = saleData.RemainingPaymentAmount ?? saleData.remainingPaymentAmount ?? null;
      
      // StatusId: 1=Aktif, 2=Tamamlandı, 4=Askıda, 7=İptal, 8=Red, 9=Timeout
      const cancelledStatusIds = [7, 8, 9];
      const isCancelled = statusId !== undefined && cancelledStatusIds.includes(statusId);
      const isCompleted = statusId === 2;
      const hasCompletedPayment = addedPayments.some((p: any) =>
        p.StatusId === 2 || p.statusId === 2 ||
        p.ResponseCode === '00' || p.responseCode === '00'
      );
      
      console.log(`[PavoPay API] 🔍 AdvanceSale Durum Kontrolü:`, {
        hasError, saleNumber, statusId, errorMessage,
        isCancelled, isCompleted,
        addedPaymentsCount: addedPayments.length,
        hasCompletedPayment, remainingPayment,
      });
      console.log(`[PavoPay API] 📦 AdvanceSale FULL RESPONSE:`, JSON.stringify(data, null, 2));
      
      // Başarısız durumlar
      if (hasError || !saleNumber || isCancelled) {
        let userMessage = '';
        if (isCancelled) {
          userMessage = statusId === 7 ? 'POS işlemi iptal edildi'
            : statusId === 8 ? 'POS işlemi reddedildi'
            : 'POS işlemi zaman aşımına uğradı';
        } else {
          userMessage = errorMessage || 'Avans POS ödemesi başarısız';
        }
        console.error(`[PavoPay API] ❌ AdvanceSale başarısız:`, userMessage);
        return {
          success: false,
          data: { ...data, Message: userMessage },
          rawResponse: response
        };
      }
      
      // Ödeme kontrolü: StatusId 2 (Tamamlandı) veya AddedPayments'ta başarılı ödeme olmalı
      // veya RemainingPaymentAmount 0 olmalı
      const paymentConfirmed = isCompleted || hasCompletedPayment || remainingPayment === 0;
      
      if (!paymentConfirmed) {
        console.error(`[PavoPay API] ❌ AdvanceSale: Ödeme tamamlanmadı. StatusId=${statusId}, Payments=${addedPayments.length}, Remaining=${remainingPayment}`);
        
        // Askıdaki satışı iptal et
        if (saleNumber) {
          (async () => {
            try {
              const stg = await getPavoPaySettings();
              if (stg) {
                await fetchWithFallback(`${getLocalServiceApiBaseUrl()}/pavopay-abandon-sale`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    posIp: stg.posIp, port: stg.port,
                    abandonRequest: {
                      TransactionHandle: { SerialNumber: stg.serialNumber, Fingerprint: stg.fingerprint, TransactionSequence: stg.sequence, TransactionDate: new Date().toISOString() },
                      Sale: { SaleNumber: saleNumber }
                    }
                  })
                });
                console.log(`[PavoPay API] 🔄 Askıdaki AdvanceSale iptal edildi: ${saleNumber}`);
              }
            } catch (e) { console.warn('[PavoPay API] Askıdaki satış iptal hatası:', e); }
          })();
        }
        
        return {
          success: false,
          data: { ...data, Message: 'POS ödemesi tamamlanmadı veya iptal edildi' },
          rawResponse: response
        };
      }
      
      console.log(`[PavoPay API] ✅ AdvanceSale başarılı - Satış No: ${saleNumber}, StatusId: ${statusId}`);
      return { success: true, data, rawResponse: response };
    }

    // 2b. GetReturnableSale (Sorgu) - HasError ve Data varlığı yeterli
    // Bu endpoint mevcut satışın bilgilerini döndürür; StatusId satışın mevcut durumunu gösterir
    // (ör. 8 = E-Arşiv kesilmiş/tamamlanmış) ve iptal anlamına gelmez.
    if (endpoint === 'GetReturnableSale') {
      const hasError = data.HasError === true;
      const saleData: any = data.Sale || data.Data || {};
      const saleNumber = saleData.SaleNumber || saleData.saleNumber || '';

      if (hasError || !saleNumber) {
        const errorMessage = data.Message || data.ErrorCode || 'Satış bilgisi alınamadı';
        console.error(`[PavoPay API] ❌ GetReturnableSale başarısız:`, errorMessage);
        return {
          success: false,
          data: { ...data, Message: errorMessage },
          rawResponse: response
        };
      }

      console.log(`[PavoPay API] ✅ GetReturnableSale başarılı - Satış No: ${saleNumber}, StatusId: ${saleData.StatusId}`);
      return { success: true, data, rawResponse: response };
    }

    // 2c. PartialReturn / PartialReturnForAdvanceSale (İade) - HasError kontrolü yeterli
    // İade işlemlerinde StatusId satışın iade durumunu gösterir, CompleteSale mantığı uygulanmaz.
    if (endpoint === 'PartialReturn' || endpoint === 'PartialReturnForAdvanceSale') {
      const hasError = data.HasError === true;
      const errorMessage = data.Message || data.ErrorCode || '';
      const errors = (data as any).Errors;

      if (hasError || errors?.length > 0) {
        const msg = errors?.join(', ') || errorMessage || 'İade işlemi başarısız';
        console.error(`[PavoPay API] ❌ ${endpoint} başarısız:`, msg);
        return {
          success: false,
          data: { ...data, Message: msg },
          rawResponse: response
        };
      }

      const saleData: any = data.Sale || data.Data || {};
      const saleNumber = saleData.SaleNumber || saleData.saleNumber || '';
      console.log(`[PavoPay API] ✅ ${endpoint} başarılı - Satış No: ${saleNumber}`);
      return { success: true, data, rawResponse: response };
    }

    // 2e. GetDeviceInfo (Cihaz bilgisi sorgusu) - Sadece HasError kontrolü yeterli
    if (endpoint === 'GetDeviceInfo') {
      const hasError = data.HasError === true;
      if (hasError) {
        const errorMessage = data.Message || data.ErrorCode || 'Cihaz bilgisi alınamadı';
        console.error(`[PavoPay API] ❌ GetDeviceInfo başarısız:`, errorMessage);
        return {
          success: false,
          data: { ...data, Message: errorMessage },
          rawResponse: response
        };
      }
      console.log(`[PavoPay API] ✅ GetDeviceInfo başarılı`);
      return { success: true, data, rawResponse: response };
    }

    // 2d. QueryCustomerInfo - Sadece HasError kontrolü, ödeme kontrolü yapılmaz
    if (endpoint === 'QueryCustomerInfo') {
      const hasError = data.HasError === true;
      if (hasError) {
        const errorMessage = data.Message || data.ErrorCode || 'Müşteri sorgusu başarısız';
        console.error(`[PavoPay API] ❌ QueryCustomerInfo başarısız:`, errorMessage);
        return {
          success: false,
          data: { ...data, Message: errorMessage },
          rawResponse: response
        };
      }
      console.log(`[PavoPay API] ✅ QueryCustomerInfo başarılı`);
      return { success: true, data, rawResponse: response };
    }
    
    // 3. CompleteSale (Satış İşlemi) - DETAYLI BAŞARI KONTROLÜ
    // 2.1. HasError kontrolü
    const hasError = data.HasError === true;
    const errorCode = data.ErrorCode || '';
    const errorMessage = data.Message || '';
    
    // 2.2. Sale/Data bilgilerini al (PavoPay bazen "Data" kullanıyor)
    const saleData: any = data.Sale || data.Data || {}; // ✅ `as any` eklendi
    
    // ✅ DÜZELTME: SaleNumber birçok yerde olabilir (pavopay-test.html'den alındı)
    const saleNumber = saleData.SaleNumber || saleData.saleNumber || 
                       (data as any).SaleNumber || (data as any).saleNumber || '';
    const invoiceNo = saleData.InvoiceNo || saleData.invoiceNo || 
                      (data as any).InvoiceNo || (data as any).invoiceNo || '';
    const statusId = saleData.StatusId || saleData.statusId || (data as any).StatusId;
    const status = saleData.Status || saleData.status || (data as any).Status || '';
    
    // 2.3. Ödeme kontrolü - ✅ DÜZELTME: AddedPayments içindeki StatusId kontrol et!
    // Birçok yerde olabilir
    const remainingPayment = saleData.RemainingPaymentAmount ?? 
                             saleData.remainingPaymentAmount ?? 
                             (data as any).RemainingPaymentAmount ?? 
                             (data as any).remainingPaymentAmount ?? 
                             null;
    const addedPayments: any[] = saleData.AddedPayments || 
                                  saleData.addedPayments || 
                                  (data as any).AddedPayments || 
                                  (data as any).addedPayments || 
                                  [];
    
    // ✅ DOĞRU KONTROL: AddedPayments içinde StatusId: 2 (Completed) olan ödeme var mı?
    // RemainingPaymentAmount güvenilir değil! (API bazen güncellememiş oluyor)
    const hasCompletedPayment = addedPayments.some(payment => 
      payment.StatusId === 2 || payment.statusId === 2 || // 2 = Completed
      payment.ResponseCode === "00" || payment.responseCode === "00" // 00 = Başarılı
    );
    
    const hasValidPayment = addedPayments.length > 0 && hasCompletedPayment;
    
    // 2.4. İptal durumları
    // StatusId: 1=Aktif, 2=Tamamlandı, 7=İptal, 8=Red, 9=Timeout (PavoPay)
    const cancelledStatusIds = [7, 8, 9];
    const isCancelledByStatus = statusId !== undefined && cancelledStatusIds.includes(statusId);
    const isCancelledByText = ['Cancelled', 'Rejected', 'canceled', 'rejected', 'İptal', 'iptal'].includes(status);
    const isCancelled = isCancelledByStatus || isCancelledByText;
    
    // 2.5. Ödeme başarısızlık durumları (ErrorCode kontrolü)
    const paymentFailureErrors = ['PaymentCancelled', 'UserCancelled', 'Timeout', 'CardReadTimeout', 'PaymentFailed'];
    const isPaymentFailed = paymentFailureErrors.some(err => errorCode.includes(err));
    
    // 2.6. Başarılı durum kontrolü (StatusId: 2 = Tamamlandı)
    const isCompletedStatus = statusId === 2;
    
    // ✅ BAŞARILI ÖDEME KRİTERLERİ (TÜM ŞARTLAR SAĞLANMALI):
    // 1. API hatası yok (!hasError)
    // 2. Satış numarası var (saleNumber)
    // 3. ÖNEMLI: En az bir ödeme eklenmiş VE kalan tutar 0 (hasValidPayment)
    // 4. İptal edilmemiş (!isCancelled)
    // 5. Ödeme hatası yok (!isPaymentFailed)
    const isSuccess = !hasError && 
                      saleNumber && 
                      hasValidPayment && 
                      !isCancelled && 
                      !isPaymentFailed;
    
    console.log(`[PavoPay API] 🔍 Detaylı Durum Kontrolü (${endpoint}):`, {
      hasError,
      errorCode,
      errorMessage,
      saleNumber,
      invoiceNo,
      statusId,
      status,
      isCompletedStatus,
      remainingPayment,
      addedPaymentsCount: addedPayments.length,
      hasCompletedPayment,
      hasValidPayment,
      isCancelledByStatus,
      isCancelledByText,
      isCancelled,
      isPaymentFailed,
      '→ isSuccess': isSuccess
    });
    
    // ✅ FULL RESPONSE'U DA LOGLA (debugging için)
    console.log(`[PavoPay API] 📦 FULL RESPONSE:`, JSON.stringify(data, null, 2));
    
    if (!isSuccess) {
      // ✅ Detaylı hata mesajı oluştur
      let userMessage = '';
      let technicalDetails = '';
      
      if (hasError) {
        userMessage = `POS Cihazı Hatası: ${errorMessage || 'Bilinmeyen hata'}`;
        technicalDetails = `ErrorCode: ${errorCode || '-'}`;
      } else if (isCancelled) {
        if (isCancelledByStatus) {
          userMessage = `İşlem İptal Edildi (StatusId: ${statusId})`;
        } else {
          userMessage = `İşlem İptal Edildi`;
        }
        technicalDetails = `Status: ${status || '-'}, StatusId: ${statusId || '-'}`;
      } else if (isPaymentFailed) {
        userMessage = `Ödeme Başarısız: ${errorCode}`;
        technicalDetails = `Lütfen kartınızı kontrol edip tekrar deneyin.`;
      } else if (!hasValidPayment) {
        if (addedPayments.length === 0) {
          userMessage = `Ödeme Alınamadı - Ödeme işlemi başlatılmadı`;
          technicalDetails = `AddedPayments: 0`;
        } else {
          // Ödeme var ama StatusId kontrolleri geçmemiş
          const paymentStatuses = addedPayments.map(p => `StatusId: ${p.StatusId || p.statusId || '?'}, ResponseCode: ${p.ResponseCode || p.responseCode || '?'}`).join(', ');
          userMessage = `Ödeme Tamamlanmadı veya Başarısız`;
          technicalDetails = `Eklenen ödeme: ${addedPayments.length}, Durumlar: [${paymentStatuses}]`;
        }
      } else if (!saleNumber) {
        userMessage = `Satış Numarası Oluşturulamadı`;
        technicalDetails = `POS cihazı satış kaydı oluşturamadı`;
      } else {
        userMessage = `Beklenmeyen Ödeme Hatası`;
        technicalDetails = `Tüm kontroller geçildi ama başarı kriteri sağlanmadı`;
      }
      
      console.error(`[PavoPay API] ❌ Satış işlemi başarısız:`, {
        userMessage,
        technicalDetails,
        fullResponse: data
      });
      
      // ✅ YENİ: Başarısız durumda askıdaki satışı otomatik iptal et
      // Eğer bir saleNumber oluştuysa ve satış tamamlanmadıysa, iptal et
      if (saleNumber && !isCancelled) {
        console.log(`[PavoPay API] 🔄 Askıdaki satış iptal ediliyor: ${saleNumber}`);
        
        // Arka planda iptal et (sonucu beklemeden devam et)
        (async () => {
          try {
            const settings = await getPavoPaySettings();
            if (settings) {
              const abandonRequest = {
                TransactionHandle: {
                  SerialNumber: settings.serialNumber,
                  Fingerprint: settings.fingerprint,
                  TransactionSequence: settings.sequence,
                  TransactionDate: new Date().toISOString()
                },
                Sale: { SaleNumber: saleNumber }
              };
              
              const abandonResponse = await fetchWithFallback(`${getLocalServiceApiBaseUrl()}/pavopay-abandon-sale`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  posIp: settings.posIp,
                  port: settings.port,
                  abandonRequest
                })
              });
              
              if (abandonResponse.ok) {
                const abandonResult = await abandonResponse.json();
                if (abandonResult.success) {
                  console.log(`[PavoPay API] ✅ Askıdaki satış iptal edildi: ${saleNumber}`);
                } else {
                  console.warn(`[PavoPay API] ⚠️ Satış iptal edilemedi: ${abandonResult.message}`);
                }
              }
            }
          } catch (abandonError) {
            console.warn(`[PavoPay API] ⚠️ Satış iptal hatası (arka plan):`, abandonError);
          }
        })();
      }
      
      // ❌ showAlertModal burada çağrılmıyor - usePayment.ts kendi modalını açacak
      // Detaylı hata mesajını data içinde döndür
      return { 
        success: false, 
        data: { 
          ...data, 
          Message: userMessage, // usePayment.ts bu mesajı kullanacak
          TechnicalDetails: technicalDetails,
          SaleNumber: saleNumber // ✅ YENİ: SaleNumber'ı da döndür (gerekirse kontrol için)
        }, 
        rawResponse: response 
      };
    }
    
    console.log(`[PavoPay API] ✅ Satış işlemi başarılı - Satış No: ${saleNumber}`);
    return { success: true, data, rawResponse: response };
    
  } catch (error: any) {
    console.error(`[PavoPay API] Genel hata:`, error);
    // ❌ showAlertModal burada çağrılmıyor - usePayment.ts kendi modalını açacak
    return { 
      success: false, 
      data: { 
        Message: `Local-service bağlantı hatası: ${error.message}`,
        TechnicalDetails: 'Local-service çalıştığından ve POS cihazının açık olduğundan emin olun.'
      }, 
      rawResponse: null 
    };
  }
};

// PavoPay POS entegrasyonu
export class PavoPayDevice implements IFiscalDevice {
  /**
   * Nakit ödeme için PavoPay işlemi
   * @param kitchenOrderCode - Mutfak modu aktifse HS-X formatında sipariş numarası (opsiyonel)
   */
  async processCashPayment(
    products: FiscalProductItem[],
    totalAmount: number,
    showAlertModal?: (title: string, message: string) => void,
    kitchenOrderCode?: string,
    receiptCompanyName?: string,
    useExternalPrinter?: boolean
  ): Promise<FiscalOperationResult> {
    // Rest-P2P aktifse local-service yerine SignalR + Pavo intent (completeSale) yolunu kullan.
    // T18: Cihaz tarafında YENİ İŞ YOK — `_processSingleAuto` zaten `receiptItemsJson`'u
    // parse edip sepet kalemlerini `completeSale` intent'ine dönüştürüyor.
    const restCfg = getRestP2PConfig();
    if (restCfg.enabled && restCfg.deviceCode) {
      try {
        const receiptItemsJson = JSON.stringify(products.map(p => ({
          name: p.name,
          quantity: p.quantity,
          unitPrice: p.price,
          vatRate: p.vatRate ?? 0,
          totalAmount: p.price * p.quantity,
        })));
        console.log('[PavoPay/RestP2P] Cash → completeSale (PYK Fiş Kes)', {
          deviceCode: restCfg.deviceCode,
          totalAmount,
          productCount: products.length,
        });
        const outcome = await restP2PPaymentService.requestPayment({
          deviceCode: restCfg.deviceCode,
          paymentMode: 'cash',
          totalAmount,
          parts: [{ method: 'cash', amount: totalAmount, order: 1 }],
          receiptItemsJson,
          companyId: restCfg.companyId,
          branchId: restCfg.branchId,
          sourceLabel: 'PYK Fiş Kes',
        });
        if (outcome.status !== 'completed') {
          const errMsg = outcome.status === 'cancelled'
            ? (outcome.reason || 'Pavo ödemesi iptal edildi')
            : (outcome.message || 'Rest-P2P Cash ödeme başarısız');
          return { success: false, errorMessage: errMsg };
        }
        const r = (outcome.result as any) || {};
        return {
          success: true,
          documentId: r.saleNumber ?? r.SaleNumber,
          fiscalData: r,
        };
      } catch (err: any) {
        console.error('[PavoPay/RestP2P] processCashPayment hatası:', err);
        return { success: false, errorMessage: err?.message || 'Rest-P2P Cash ödeme hatası' };
      }
    }

    try {
      console.log('[PavoPay] Nakit ödeme işlemi başlıyor...', { totalAmount, productCount: products.length, kitchenOrderCode, receiptCompanyName });
      
      const settings = await getPavoPaySettings(true);
      if (!settings) {
        return {
          success: false,
          errorMessage: 'PavoPay ayarları bulunamadı'
        };
      }

      // Pairing ile cihazı senkronize et (sequence hatası önleme)
      console.log('[PavoPay] Nakit: Pairing ile cihaz senkronize ediliyor...');
      const pairingResult = await callPavoPayAPI('Pairing', {
        TransactionHandle: {
          SerialNumber: settings.serialNumber,
          Fingerprint: settings.fingerprint,
          TransactionSequence: 1,
          TransactionDate: new Date().toISOString()
        }
      });

      if (pairingResult.success) {
        const pairingData = pairingResult.data as any;
        const deviceSeq = pairingData?.TransactionHandle?.TransactionSequence;
        if (deviceSeq && typeof deviceSeq === 'number') {
          const syncedSeq = deviceSeq + 1;
          settings.sequence = syncedSeq;
          if (cachedSettings) cachedSettings.sequence = syncedSeq;
          updateSequence(syncedSeq).catch(() => {});
          console.log(`[PavoPay] Nakit: Cihaz sequence senkronize edildi: device=${deviceSeq}, next=${syncedSeq}`);
        }
      }
      
      // ✅ Mutfak modu aktifse fişe şirket adı/sipariş numarası üstüne + AQUASOFT altına
      const { top: topPrintableItems, bottom: bottomPrintableItems } =
        buildKitchenPrintableItems(kitchenOrderCode, receiptCompanyName);
      
      // Sale Request oluştur
      const hasPhone = !!pendingNotificationPhone;
      const saleRequest: PavoPaySaleRequest = {
        TransactionHandle: createTransactionHandle(settings), // ✅ await kaldırıldı
        Sale: {
          RefererApp: 'GTS Yazılım',
          RefererAppVersion: '1.0.0',
          MainDocumentType: getMainDocumentType(), // 1=E-Fatura, 2=E-Arşiv (kasiyerin seçimine göre)
          GrossPrice: totalAmount,
          TotalPrice: totalAmount,
          SendPhoneNotification: hasPhone,
          SendEMailNotification: false,
          ...(hasPhone && { NotificationPhone: pendingNotificationPhone }),
          SkipAmountCash: true, // Nakit ise tutar sormadan direkt kes
          ...(topPrintableItems && { TopPrintableItems: topPrintableItems, BottomPrintableItems: bottomPrintableItems }),
          ShowCreditCardMenu: false,
          AskCustomer: false,
          ...(pendingCustomerParty ? { CustomerParty: buildSaleCustomerParty(pendingCustomerParty) } : {}),
          SelectedSlots: ['rf', 'icc', 'manual', 'qr', 'magneticStripe'],
          CardReadTimeout: 40,
          CancelPaymentLater: true,
          TryAgainOnPaymentFailure: false,
          AddedSaleItems: products.map(product => {
            const priceWithVat = product.price;

            const totalWithVat = priceWithVat * product.quantity;
            
            // ✅ FIX: vatRate undefined olduğunda güvenli TaxGroupCode hesaplama
            const vatRate = product.vatRate ?? 0;
            const taxGroupCode = vatRate === 20 ? 'KDV20' : 
                                vatRate === 10 ? 'KDV10' : 
                                vatRate === 8 ? 'KDV8' : 
                                vatRate === 1 ? 'KDV1' : 'KDV0';
            
            console.log('[PavoPay] 🔍 ÜRÜN FİYAT DEBUG:', {
              name: product.name,
              priceWithVat: priceWithVat, // ✅ Artık zaten KDV dahil geliyor
              quantity: product.quantity,
              vatRate: vatRate,
              taxGroupCode: taxGroupCode,
              totalWithVat: totalWithVat
            });
            
            return {
              Name: product.name,
              IsGeneric: false,
              UnitCode: 'C62', // Adet
              TaxGroupCode: taxGroupCode, // ✅ FIX: Güvenli TaxGroupCode
              ItemQuantity: product.quantity,
              UnitPriceAmount: priceWithVat,        // ✅ KDV DAHİL birim fiyat
              GrossPriceAmount: totalWithVat,       // ✅ KDV DAHİL toplam
              TotalPriceAmount: totalWithVat        // ✅ KDV DAHİL toplam
            };
          }),
          ReceiptInformation: {
            PrintCustomerReceipt: true,
            PrintCustomerReceiptCopy: false,
            PrintMerchantReceipt: true,
            ReceiptWidth: useExternalPrinter ? '80mm' : '58mm',
          },
          PaymentInformations: [{
            Mediator: 1, // 1 = Nakit
            Amount: totalAmount,
            CurrencyCode: 'TRY',
            ExchangeRate: 1
          }],
          AllowedPaymentMediators: [{ Mediator: 1 }]
        }
      };
      
      if (saleRequest.Sale?.CustomerParty) {
        console.log('[PavoPay] CustomerParty gönderiliyor:', JSON.stringify(saleRequest.Sale.CustomerParty));
      } else {
        console.log('[PavoPay] CustomerParty YOK, genel cari olarak kesilecek');
      }
      console.log('[PavoPay] MainDocumentType:', saleRequest.Sale.MainDocumentType === 1 ? 'E-Fatura (1)' : 'E-Arşiv (2)');
      console.log('[PavoPay] Sale Request oluşturuldu:', saleRequest);
      
      // CompleteSale API'yi çağır
      const result = await callPavoPayAPI('CompleteSale', saleRequest, showAlertModal);
      
      if (!result.success) {
        return {
          success: false,
          errorMessage: result.data?.Message || 'Satış işlemi başarısız'
        };
      }
      
      const response = result.data as PavoPayResponse;
      const saleData: any = response.Sale || response.Data || {};
      const ettn = extractETTNFromResponse(saleData);
      const inquiryLink = saleData?.SaleInquieryLink || saleData?.saleInquieryLink || '';
      
      pendingCustomerParty = null;
      pendingIsEInvoice = false;
      pendingNotificationPhone = '';
      console.log('[PavoPay] Nakit ödeme başarılı:', response.Sale);
      
      return {
        success: true,
        documentId: response.Sale?.SaleNumber || saleData?.SaleNumber || 'N/A',
        fiscalData: {
          SaleNumber: response.Sale?.SaleNumber || saleData?.SaleNumber,
          InvoiceNo: response.Sale?.InvoiceNo || saleData?.InvoiceNo,
          Status: response.Sale?.Status || saleData?.Status,
          EInvoiceETTN: ettn,
          PavoPayInquiryLink: inquiryLink ? (inquiryLink.startsWith('http') ? inquiryLink : `https://${inquiryLink}`) : undefined,
        }
      };
      
    } catch (error: any) {
      console.error('[PavoPay] Nakit ödeme hatası:', error);
      // ❌ showAlertModal burada çağrılmıyor - usePayment.ts kendi modalını açacak
      return {
        success: false,
        errorMessage: error.message || 'PavoPay nakit ödeme hatası'
      };
    }
  }

  /**
   * POS ödeme için PavoPay işlemi
   * @param kitchenOrderCode - Mutfak modu aktifse HS-X formatında sipariş numarası (opsiyonel)
   */
  async processPosPayment(
    products: FiscalProductItem[],
    totalAmount: number,
    showAlertModal?: (title: string, message: string) => void,
    cashAmount?: number,
    companyId?: string,
    kitchenOrderCode?: string,
    receiptCompanyName?: string,
    useExternalPrinter?: boolean
  ): Promise<FiscalOperationResult> {
    try {
      console.log('[PavoPay] POS ödeme işlemi başlıyor...', { 
        totalAmount, 
        cashAmount, 
        productCount: products.length,
        companyId,
        kitchenOrderCode,
        receiptCompanyName
      });
      
      const settings = await getPavoPaySettings(); // ✅ await eklendi
      if (!settings) {
        return {
          success: false,
          errorMessage: 'PavoPay ayarları bulunamadı'
        };
      }
      
      // ✅ Mutfak modu aktifse fişe şirket adı/sipariş numarası üstüne + AQUASOFT altına
      const { top: topPrintableItems, bottom: bottomPrintableItems } =
        buildKitchenPrintableItems(kitchenOrderCode, receiptCompanyName);
      
      // Ödeme bilgilerini hazırla
      const paymentInformations: any[] = [];
      const allowedMediators: any[] = [];
      
      if (cashAmount && cashAmount > 0) {
        // Karma ödeme: POS + Nakit
        paymentInformations.push({
          Mediator: 2, // 2 = Kredi Kartı
          Amount: totalAmount - cashAmount,
          CurrencyCode: 'TRY',
          ExchangeRate: 1
        });
        paymentInformations.push({
          Mediator: 1, // 1 = Nakit
          Amount: cashAmount,
          CurrencyCode: 'TRY',
          ExchangeRate: 1
        });
        allowedMediators.push({ Mediator: 2 }, { Mediator: 1 });
      } else {
        // Sadece POS
        paymentInformations.push({
          Mediator: 2, // 2 = Kredi Kartı
          Amount: totalAmount,
          CurrencyCode: 'TRY',
          ExchangeRate: 1
        });
        allowedMediators.push({ Mediator: 2 });
      }
      
      // ✅ YENİ: Benzersiz OrderNo oluştur (timeout durumunda satışı sorgulamak için)
      const orderNo = `GTS-${Date.now()}-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
      console.log('[PavoPay] 📝 OrderNo oluşturuldu:', orderNo);
      
      // Sale Request oluştur
      const saleRequest: PavoPaySaleRequest = {
        TransactionHandle: createTransactionHandle(settings), // ✅ await kaldırıldı
        Sale: {
          RefererApp: 'GTS Yazılım',
          RefererAppVersion: '1.0.0',
          OrderNo: orderNo, // ✅ YENİ: Timeout durumunda satışı sorgulamak için
          MainDocumentType: getMainDocumentType(), // 1=E-Fatura, 2=E-Arşiv (kasiyerin seçimine göre)
          GrossPrice: totalAmount,
          TotalPrice: totalAmount,
          SendPhoneNotification: !!pendingNotificationPhone,
          SendEMailNotification: false,
          ...(pendingNotificationPhone && { NotificationPhone: pendingNotificationPhone }),
          SkipAmountCash: false, // POS için false
          ...(topPrintableItems && { TopPrintableItems: topPrintableItems, BottomPrintableItems: bottomPrintableItems }),
          ShowCreditCardMenu: false,
          AskCustomer: false,
          ...(pendingCustomerParty ? { CustomerParty: buildSaleCustomerParty(pendingCustomerParty) } : {}),
          SelectedSlots: ['rf', 'icc', 'manual', 'qr', 'magneticStripe'],
          CardReadTimeout: 40,
          CancelPaymentLater: true,
          TryAgainOnPaymentFailure: false,
          AddedSaleItems: products.map(product => {
            const vatRate = product.vatRate ?? 0;
            const priceWithVat = product.price;
            const totalWithVat = priceWithVat * product.quantity;
            
            // ✅ FIX: vatRate undefined olduğunda güvenli TaxGroupCode hesaplama
            const taxGroupCode = vatRate === 20 ? 'KDV20' : 
                                vatRate === 10 ? 'KDV10' : 
                                vatRate === 8 ? 'KDV8' : 
                                vatRate === 1 ? 'KDV1' : 'KDV0';
            
            console.log('[PavoPay] 🔍 ÜRÜN FİYAT DEBUG (POS):', {
              name: product.name,
              priceWithVat: priceWithVat, // ✅ Artık zaten KDV dahil geliyor
              quantity: product.quantity,
              vatRate: vatRate,
              taxGroupCode: taxGroupCode,
              totalWithVat: totalWithVat
            });
            
            return {
              Name: product.name,
              IsGeneric: false,
              UnitCode: 'C62',
              TaxGroupCode: taxGroupCode, // ✅ FIX: Güvenli TaxGroupCode
              ItemQuantity: product.quantity,
              UnitPriceAmount: priceWithVat,        // ✅ KDV DAHİL birim fiyat
              GrossPriceAmount: totalWithVat,       // ✅ KDV DAHİL toplam
              TotalPriceAmount: totalWithVat        // ✅ KDV DAHİL toplam
            };
          }),
          ReceiptInformation: {
            PrintCustomerReceipt: true,
            PrintCustomerReceiptCopy: false,
            PrintMerchantReceipt: true,
            ReceiptWidth: useExternalPrinter ? '80mm' : '58mm',
          },
          PaymentInformations: paymentInformations,
          AllowedPaymentMediators: allowedMediators
        }
      };
      
      if (saleRequest.Sale?.CustomerParty) {
        console.log('[PavoPay] POS CustomerParty gönderiliyor:', JSON.stringify(saleRequest.Sale.CustomerParty));
      } else {
        console.log('[PavoPay] POS CustomerParty YOK, genel cari olarak kesilecek');
      }
      console.log('[PavoPay] POS Sale Request oluşturuldu:', saleRequest);
      
      // CompleteSale API'yi çağır
      const result = await callPavoPayAPI('CompleteSale', saleRequest, showAlertModal);
      
      if (!result.success) {
        // ✅ YENİ: Timeout durumunda OrderNo ile satışı sorgula ve gerekirse iptal et
        const errorMessage = result.data?.Message || 'POS işlemi başarısız';
        const isTimeout = errorMessage.includes('504') || errorMessage.includes('timeout') || errorMessage.includes('Timeout');
        
        if (isTimeout) {
          console.log('%c[PavoPay] ⏱️ TIMEOUT ALGILANDI - Satış durumu kontrolü başlıyor...', 'background: #ff6b6b; color: white; font-size: 14px; padding: 4px 8px;');
          console.log('[PavoPay] 📋 OrderNo:', orderNo);
          
          // ✅ YENİ: Retry mekanizması - cihaz boşalana kadar bekle
          const maxRetries = 5;
          const retryDelayMs = 3000; // 3 saniye
          
          for (let attempt = 1; attempt <= maxRetries; attempt++) {
            console.log(`[PavoPay] ⏳ Deneme ${attempt}/${maxRetries} - ${retryDelayMs/1000} saniye bekleniyor...`);
            await new Promise(resolve => setTimeout(resolve, retryDelayMs));
            
            try {
              console.log('%c[PavoPay] 🔍 GetSaleResult ÇAĞRILIYOR...', 'background: #4ecdc4; color: white; font-size: 12px; padding: 2px 6px;');
              console.log('[PavoPay] 📤 OrderNo:', orderNo);
              const saleResult = await getSaleResultByOrderNo(orderNo);
              
              // ✅ GetSaleResult sonucunu belirgin şekilde logla
              console.log('%c[PavoPay] 📥 GetSaleResult SONUCU:', 'background: #6c5ce7; color: white; font-size: 12px; padding: 2px 6px;');
              console.log('[PavoPay] 📥 success:', saleResult.success);
              console.log('[PavoPay] 📥 statusId:', saleResult.statusId);
              console.log('[PavoPay] 📥 saleNumber:', saleResult.saleNumber);
              console.log('[PavoPay] 📥 errorMessage:', saleResult.errorMessage);
              
              // "İşlem devam etmektedir" = Cihaz hala meşgul, bekle ve tekrar dene
              // ⚠️ ÖNEMLİ: Bu durumda success: false ve statusId yok!
              if (saleResult.errorMessage?.includes('devam')) {
                console.log(`%c[PavoPay] 🔄 Cihaz meşgul (deneme ${attempt}/${maxRetries}) - Tekrar denenecek...`, 'color: orange; font-weight: bold;');
                continue; // Sonraki denemeye geç
              }
              
              // Cihaz boşaldı, gerçek sonuç geldi
              if (saleResult.success && saleResult.saleNumber) {
                console.log('%c[PavoPay] 📊 GERÇEK SATIŞ DURUMU ALINDI:', 'background: #00b894; color: white; font-size: 12px; padding: 2px 6px;');
                console.log('[PavoPay] 📊 StatusId:', saleResult.statusId, '| SaleNumber:', saleResult.saleNumber);
                
                // StatusId: 2 = Tamamlandı
                if (saleResult.statusId === 2) {
                  console.log('%c[PavoPay] ✅ SATIŞ TAMAMLANMIŞ! (StatusId: 2) - Kurtarılıyor...', 'background: #27ae60; color: white; font-size: 14px; padding: 4px 8px;');
                  
                  // Başarılı satışı kaydet
                  if (companyId) {
                    savePavoPaySale({
                      saleNumber: saleResult.saleNumber,
                      invoiceNo: saleResult.invoiceNo || '',
                      amount: totalAmount,
                      date: new Date().toISOString(),
                      paymentType: cashAmount ? 'KARMA' : 'POS',
                      items: products.map(p => ({ name: p.name, quantity: p.quantity, price: p.price })),
                      fiscalData: saleResult.rawData
                    }, companyId);
                  }
                  
                  console.log('[PavoPay] 🎉 Satış timeout sonrası kurtarıldı!');
                  return {
                    success: true,
                    documentId: saleResult.saleNumber,
                    fiscalData: {
                      SaleNumber: saleResult.saleNumber,
                      InvoiceNo: saleResult.invoiceNo,
                      Status: 'Completed',
                      PaymentType: cashAmount ? 'KARMA' : 'POS',
                      EInvoiceETTN: saleResult.ettn,
                    }
                  };
                } else if (saleResult.statusId === 1) {
                  // StatusId: 1 = Askıda (cihaz boşaldı, satış askıya alındı)
                  console.log('%c[PavoPay] 📦 SATIŞ ASKIDA! (StatusId: 1) - AbandonSuspendedSale çağrılacak...', 'background: #f39c12; color: white; font-size: 14px; padding: 4px 8px;');
                  
                  const abandonResult = await abandonPavoPaySale(saleResult.saleNumber, false); // SaleNumber ile iptal
                  
                  if (abandonResult.success) {
                    console.log('[PavoPay] ✅ Askıdaki satış başarıyla iptal edildi!');
                    return {
                      success: false,
                      errorMessage: 'İşlem zaman aşımına uğradı ve iptal edildi. Lütfen tekrar deneyin.'
                    };
                  } else {
                    console.warn('[PavoPay] ⚠️ Satış iptal edilemedi:', abandonResult.errorMessage);
                  }
                } else {
                  // Diğer durumlar (6=Beklemede, 7=İptal, 8=Red, 9=Timeout)
                  console.log(`%c[PavoPay] ⚠️ SATIŞ TAMAMLANMADI (StatusId: ${saleResult.statusId})`, 'background: #e74c3c; color: white; font-size: 14px; padding: 4px 8px;');
                  
                  if (saleResult.statusId === 7 || saleResult.statusId === 9) {
                    // İptal veya Timeout - zaman aşımı mesajı göster
                    console.log('[PavoPay] ℹ️ Satış iptal/timeout - AbandonSuspendedSale ÇAĞRILMAYACAK');
                    return {
                      success: false,
                      errorMessage: 'İşlem zaman aşımına uğradı. Lütfen tekrar deneyin.'
                    };
                  } else if (saleResult.statusId === 8) {
                    // Reddedildi - kart hatası mesajı göster
                    console.log('[PavoPay] ℹ️ Satış reddedildi - AbandonSuspendedSale ÇAĞRILMAYACAK');
                    return {
                      success: false,
                      errorMessage: 'Ödeme reddedildi. Lütfen kartınızı kontrol edip tekrar deneyin.'
                    };
                  } else if (saleResult.statusId === 6) {
                    // Beklemede - bekle mesajı göster
                    console.log('[PavoPay] ℹ️ Satış beklemede - AbandonSuspendedSale ÇAĞRILMAYACAK');
                    return {
                      success: false,
                      errorMessage: 'İşlem beklemede. Lütfen biraz bekleyip tekrar deneyin.'
                    };
                  }
                }
              } else if (!saleResult.success && !saleResult.errorMessage?.includes('devam')) {
                // Satış bulunamadı (henüz oluşturulmamış veya hata)
                console.log('[PavoPay] 📭 OrderNo ile satış bulunamadı:', saleResult.errorMessage);
                break; // Döngüden çık
              }
            } catch (checkError) {
              console.warn(`[PavoPay] ⚠️ Satış durumu kontrol hatası (deneme ${attempt}):`, checkError);
            }
          }
          
          console.log('%c[PavoPay] ⏱️ MAKSIMUM DENEME SAYISINA ULAŞILDI - GetSaleResult kontrolü tamamlandı', 'background: #95a5a6; color: white; font-size: 12px; padding: 2px 6px;');
        }
        
        return {
          success: false,
          errorMessage
        };
      }
      
      const response = result.data as PavoPayResponse;
      
      // ✅ SaleNumber ve InvoiceNo'yu birden fazla yerden kontrol et
      const saleData: any = response.Sale || response.Data || {};
      const saleNumber = saleData.SaleNumber || saleData.saleNumber || '';
      const invoiceNo = saleData.InvoiceNo || saleData.invoiceNo || 
                       saleData.FinancialDocuments?.[0]?.InvoiceNo || '';
      const ettn = extractETTNFromResponse(saleData);
      
      pendingCustomerParty = null;
      pendingIsEInvoice = false;
      pendingNotificationPhone = '';
      console.log('[PavoPay] POS ödeme başarılı:', { saleNumber, invoiceNo, ettn, saleData });
      
      // ✅ YENİ: Başarılı satışı localStorage'a kaydet (iade için)
      if (companyId && saleNumber) {
        console.log('[PavoPay] 💾 Satış kaydediliyor:', { saleNumber, invoiceNo, companyId });
        savePavoPaySale({
          saleNumber,
          invoiceNo: invoiceNo || '',
          amount: totalAmount,
          date: new Date().toISOString(),
          paymentType: cashAmount ? 'KARMA' : 'POS',
          items: products.map(p => ({ name: p.name, quantity: p.quantity, price: p.price })),
          fiscalData: response
        }, companyId);
        console.log('[PavoPay] ✅ Satış kaydedildi!');
      } else {
        console.warn('[PavoPay] ⚠️ Satış kaydedilemedi - companyId veya saleNumber eksik:', { companyId, saleNumber });
      }
      
      const posInquiryLink = saleData?.SaleInquieryLink || saleData?.saleInquieryLink || '';
      return {
        success: true,
        documentId: saleNumber || 'N/A',
        fiscalData: {
          SaleNumber: saleNumber,
          InvoiceNo: invoiceNo,
          Status: saleData.Status,
          PaymentType: cashAmount ? 'KARMA' : 'POS',
          EInvoiceETTN: ettn,
          PavoPayInquiryLink: posInquiryLink ? (posInquiryLink.startsWith('http') ? posInquiryLink : `https://${posInquiryLink}`) : undefined,
        }
      };
      
    } catch (error: any) {
      console.error('[PavoPay] POS ödeme hatası:', error);
      // ❌ showAlertModal burada çağrılmıyor - usePayment.ts kendi modalını açacak
      return {
        success: false,
        errorMessage: error.message || 'PavoPay POS ödeme hatası'
      };
    }
  }

  /**
   * Yemek karti odeme islemi - ExternalPayments ile Brand belirterek gonderilir
   * Mediator: 10 (Harici Yemek Karti), Brand: 6-12 arasi (Multinet, Metropol, Pluxee vb.)
   */
  async processMealCardPayment(
    products: FiscalProductItem[],
    totalAmount: number,
    mealCardLabel: string,
    showAlertModal?: (title: string, message: string) => void,
    companyId?: string,
    useExternalPrinter?: boolean
  ): Promise<FiscalOperationResult> {
    const MEAL_CARD_BRAND_MAP: Record<string, number> = {
      'MULTINET': 6,
      'METROPOL': 7,
      'PLUXEE': 8,
      'TICKET': 9,
      'SETCARD': 10,
      'TOKENFLEX': 11,
      'PAYE': 12,
      'EDENRED': 9,
    };

    try {
      const brandId = MEAL_CARD_BRAND_MAP[mealCardLabel.toUpperCase()] ?? 1;
      console.log('[PavoPay] Yemek kartı ödeme başlıyor...', { totalAmount, mealCardLabel, brandId, productCount: products.length });

      const settings = await getPavoPaySettings(true);
      if (!settings) {
        return { success: false, errorMessage: 'PavoPay ayarları bulunamadı' };
      }

      // Pairing ile cihazı senkronize et
      const pairingResult = await callPavoPayAPI('Pairing', {
        TransactionHandle: {
          SerialNumber: settings.serialNumber,
          Fingerprint: settings.fingerprint,
          TransactionSequence: 1,
          TransactionDate: new Date().toISOString()
        }
      });

      if (pairingResult.success) {
        const pairingData = pairingResult.data as any;
        const deviceSeq = pairingData?.TransactionHandle?.TransactionSequence;
        if (deviceSeq && typeof deviceSeq === 'number') {
          const syncedSeq = deviceSeq + 1;
          settings.sequence = syncedSeq;
          if (cachedSettings) cachedSettings.sequence = syncedSeq;
          updateSequence(syncedSeq).catch(() => {});
          console.log(`[PavoPay] Yemek kartı: Cihaz sequence senkronize edildi: device=${deviceSeq}, next=${syncedSeq}`);
        }
      }

      const saleRequest: PavoPaySaleRequest = {
        TransactionHandle: createTransactionHandle(settings),
        Sale: {
          RefererApp: 'GTS Yazılım',
          RefererAppVersion: '1.0.0',
          MainDocumentType: getMainDocumentType(), // 1=E-Fatura, 2=E-Arşiv
          GrossPrice: totalAmount,
          TotalPrice: totalAmount,
          SendPhoneNotification: !!pendingNotificationPhone,
          SendEMailNotification: false,
          ...(pendingNotificationPhone && { NotificationPhone: pendingNotificationPhone }),
          SkipAmountCash: true,
          ShowCreditCardMenu: false,
          AskCustomer: false,
          SelectedSlots: ['rf', 'icc', 'manual', 'qr', 'magneticStripe'],
          CardReadTimeout: 60,
          CancelPaymentLater: true,
          TryAgainOnPaymentFailure: false,
          AddedSaleItems: products.map(product => {
            const priceWithVat = product.price;
            const totalWithVat = priceWithVat * product.quantity;
            const vatRate = product.vatRate ?? 0;
            const taxGroupCode = vatRate === 20 ? 'KDV20' :
                                vatRate === 10 ? 'KDV10' :
                                vatRate === 8 ? 'KDV8' :
                                vatRate === 1 ? 'KDV1' : 'KDV0';
            return {
              Name: product.name,
              IsGeneric: false,
              UnitCode: 'C62',
              TaxGroupCode: taxGroupCode,
              ItemQuantity: product.quantity,
              UnitPriceAmount: priceWithVat,
              GrossPriceAmount: totalWithVat,
              TotalPriceAmount: totalWithVat
            };
          }),
          ReceiptInformation: {
            PrintCustomerReceipt: true,
            PrintCustomerReceiptCopy: false,
            PrintMerchantReceipt: true,
            ReceiptWidth: useExternalPrinter ? '80mm' : '58mm',
          },
          ExternalPayments: [{
            Type: 3,
            Mediator: 10,
            Brand: brandId,
            Amount: totalAmount,
            CurrencyCode: 'TRY',
            ExchangeRate: 1
          }]
        }
      };

      console.log('[PavoPay] Yemek kartı Sale Request:', JSON.stringify(saleRequest, null, 2));

      const result = await callPavoPayAPI('CompleteSale', saleRequest, showAlertModal);

      if (!result.success) {
        return {
          success: false,
          errorMessage: result.data?.Message || 'Yemek kartı satış işlemi başarısız'
        };
      }

      const response = result.data as PavoPayResponse;
      const mealSaleData: any = response.Sale || response.Data || {};
      const mealEttn = extractETTNFromResponse(mealSaleData);
      const mealInquiryLink = mealSaleData?.SaleInquieryLink || mealSaleData?.saleInquieryLink || '';
      console.log('[PavoPay] Yemek kartı ödeme başarılı:', response.Sale);

      return {
        success: true,
        documentId: response.Sale?.SaleNumber || mealSaleData?.SaleNumber || 'N/A',
        fiscalData: {
          SaleNumber: response.Sale?.SaleNumber || mealSaleData?.SaleNumber,
          InvoiceNo: response.Sale?.InvoiceNo || mealSaleData?.InvoiceNo,
          Status: response.Sale?.Status || mealSaleData?.Status,
          EInvoiceETTN: mealEttn,
          PavoPayInquiryLink: mealInquiryLink ? (mealInquiryLink.startsWith('http') ? mealInquiryLink : `https://${mealInquiryLink}`) : undefined,
        }
      };
    } catch (error: any) {
      console.error('[PavoPay] Yemek kartı ödeme hatası:', error);
      return {
        success: false,
        errorMessage: error.message || 'PavoPay yemek kartı ödeme hatası'
      };
    }
  }

  /**
   * PavoPay bağlantı kontrolü (Pairing) - Local-Service Proxy üzerinden
   */
  async checkConnection(): Promise<boolean> {
    try {
      // ✅ İlk bağlantı kontrolünde fresh data çek (cache bypass)
      const settings = await getPavoPaySettings(true);
      if (!settings) {
        console.warn('[PavoPay checkConnection] Ayarlar bulunamadı');
        return false;
      }
      
      console.log('[PavoPay checkConnection] Bağlantı kontrol ediliyor (via local-service proxy)...');
      
      // ✅ Pairing için özel TransactionHandle (sequence cache'den etkilenmesin)
      const result = await callPavoPayAPI('Pairing', {
        TransactionHandle: {
          SerialNumber: settings.serialNumber,
          Fingerprint: settings.fingerprint,
          TransactionSequence: 1, // Pairing için her zaman 1 kullan
          TransactionDate: new Date().toISOString()
        }
      });
      
      if (result.success) {
        console.log('[PavoPay checkConnection] ✅ Bağlantı başarılı');
        return true;
      } else {
        console.warn('[PavoPay checkConnection] ❌ Bağlantı başarısız:', result.data);
        return false;
      }
      
    } catch (error) {
      console.error('[PavoPay checkConnection] Hata:', error);
      return false;
    }
  }

  /**
   * PavoPay cihaz bilgilerini al
   */
  async getDeviceInfo(): Promise<any> {
    try {
      const settings = await getPavoPaySettings(); // ✅ await eklendi
      if (!settings) {
        return null;
      }
      
      return {
        isConnected: await this.checkConnection(),
        posIp: settings.posIp,
        port: settings.port,
        serialNumber: settings.serialNumber,
        deviceType: 'PavoPay POS'
      };
    } catch (error) {
      console.error('[PavoPay] Cihaz bilgisi alınamadı:', error);
      return null;
    }
  }
}

// Singleton instance
export const pavopayDevice: IFiscalDevice = new PavoPayDevice();

// ============================================
// AVANS SATIŞ (PYK Bakiye Yükleme POS Ödemesi)
// ============================================

/**
 * PavoPay AdvanceSale - PYK ekranından avans POS ödemesi almak için kullanılır.
 * Ürün bilgisi içermez, sadece tutar ve ödeme yöntemi gönderilir.
 */
export interface AdvanceSaleResult {
  success: boolean;
  /**
   * Kullanıcı Pavo cihazında "Vazgeç" basıp işlemi iptal ettiğinde true olur.
   * UI bu durumu "hata" yerine "iptal" olarak gösterebilsin diye ayrılmıştır.
   */
  cancelled?: boolean;
  saleNumber?: string;
  orderNo?: string;
  invoiceNo?: string;
  fiscalTransactionData?: string;
  errorMessage?: string;
}

export const processAdvanceSale = async (
  amount: number,
  reason: string,
  paymentMediator: number = 2
): Promise<AdvanceSaleResult> => {
  // Rest-P2P aktifse local-service yerine SignalR + Pavo intent yolunu kullan.
  // PYK ayarlarından beslenen `restP2PConfig` enabled+deviceCode varsa branch'lenir.
  // OrderNo frontend'de üretilir (REST yoluyla aynı `PYK-${ts}-${rand}` formatı) —
  // backend payload'una konup cihaz Sale objesinde `OrderNo` olarak iletilir.
  const restCfg = getRestP2PConfig();
  if (restCfg.enabled && restCfg.deviceCode) {
    try {
      const orderNo = `PYK-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`;
      console.log('[PavoPay/RestP2P] AdvanceSale → POST /refund/advance-sale', {
        deviceCode: restCfg.deviceCode,
        amount,
        orderNo,
        paymentMediator,
      });
      const outcome = await restP2PPaymentService.requestAdvanceSale({
        deviceCode: restCfg.deviceCode,
        amount,
        reason: reason || 'PYK Bakiye Yükleme',
        paymentMediator,
        orderNo,
        companyId: restCfg.companyId,
        branchId: restCfg.branchId,
      });
      if (outcome.status !== 'completed') {
        // Cihaz "Vazgeç" durumunda errorCode='CANCELLED_BY_USER' gönderiyor.
        // Bu durumu kullanıcıya açık bir mesajla göster; backend'e/UI'a
        // teknik hata gibi yansımasın. `cancelled` flag UI tarafının
        // waitingCancelled modunu açabilmesi için ayrıca set edilir.
        const isUserCancel = outcome.errorCode === 'CANCELLED_BY_USER';
        const errorMessage = isUserCancel
          ? (outcome.message || 'Kullanıcı POS ekranında iptal etti')
          : (outcome.message || 'Rest-P2P AdvanceSale başarısız');
        console.warn(
          `[PavoPay/RestP2P] AdvanceSale ${isUserCancel ? 'iptal edildi' : 'başarısız'}: ${outcome.errorCode} - ${outcome.message}`,
        );
        return { success: false, cancelled: isUserCancel, errorMessage };
      }
      const r = (outcome.result as any) || {};
      return {
        success: true,
        saleNumber: r.saleNumber,
        orderNo: r.orderNo ?? orderNo,
        invoiceNo: r.invoiceNo,
        fiscalTransactionData: r.fiscalTransactionData ?? JSON.stringify(r),
      };
    } catch (err: any) {
      console.error('[PavoPay/RestP2P] AdvanceSale hatası:', err);
      return { success: false, errorMessage: err?.message || 'Rest-P2P AdvanceSale hatası' };
    }
  }

  try {
    const settings = await getPavoPaySettings(true);
    if (!settings) {
      return { success: false, errorMessage: 'PavoPay ayarları bulunamadı. Lütfen local-service yazarkasa kurulum sayfasından ayarları yapın.' };
    }

    // Adım 1: Pairing ile cihazı senkronize et (sequence sıfırlama)
    console.log('[PavoPay] AdvanceSale: Pairing ile cihaz senkronize ediliyor...');
    const pairingResult = await callPavoPayAPI('Pairing', {
      TransactionHandle: {
        SerialNumber: settings.serialNumber,
        Fingerprint: settings.fingerprint,
        TransactionSequence: 1,
        TransactionDate: new Date().toISOString()
      }
    });

    if (!pairingResult.success) {
      return { success: false, errorMessage: 'PavoPay cihazına bağlanılamadı. Cihazın açık ve bağlı olduğundan emin olun.' };
    }

    // Pairing response'ından cihaz sequence'ını al ve senkronize et
    const pairingData = pairingResult.data as any;
    const deviceSeq = pairingData?.TransactionHandle?.TransactionSequence;
    if (deviceSeq && typeof deviceSeq === 'number') {
      const syncedSeq = deviceSeq + 1;
      settings.sequence = syncedSeq;
      if (cachedSettings) cachedSettings.sequence = syncedSeq;
      updateSequence(syncedSeq).catch(() => {});
      console.log(`[PavoPay] AdvanceSale: Cihaz sequence senkronize edildi: device=${deviceSeq}, next=${syncedSeq}`);
    }

    // Adım 2: AdvanceSale gönder
    const handle = createTransactionHandle(settings);
    const orderNo = `PYK-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`;

    const saleRequest = {
      TransactionHandle: handle,
      Sale: {
        RefererApp: 'GTS PYK',
        RefererAppVersion: '1.0.0',
        MainDocumentType: getMainDocumentType(), // 1=E-Fatura, 2=E-Arşiv
        TotalAmount: amount,
        GrossPrice: amount,
        TotalPrice: amount,
        CurrencyCode: 'TRY',
        ExchangeRate: 1.0,
        IsSilentMode: false,
        AskCustomer: false,
        AskPreAuth: false,
        AskPaymentType: false,
        ShowCreditCardMenu: false,
        SkipAmountCash: true,
        SkipConfirmation: true,
        AutoComplete: true,
        SendResponseBeforePrint: true,
        ReferOtherMediatorsToRetryPayment: false,
        ReceiptInformation: {
          PrintCustomerReceipt: true,
          PrintCustomerReceiptCopy: false,
          PrintMerchantReceipt: true,
          ReceiptWidth: '58mm',
        },
        SaleReason: reason || 'PYK Bakiye Yükleme',
        OrderNo: orderNo,
        IsGenericSale: true,
        SelectedSlots: ['rf', 'icc', 'manual', 'qr', 'magneticStripe'],
        CardReadTimeout: 60,
        PaymentInformations: [{
          Mediator: paymentMediator,
          Amount: amount,
          CurrencyCode: 'TRY',
          ExchangeRate: 1
        }],
        AllowedPaymentMediators: [{ Mediator: paymentMediator }]
      }
    };

    console.log('[PavoPay] AdvanceSale request:', saleRequest);

    const result = await callPavoPayAPI('AdvanceSale', saleRequest);

    // Adım 3: Response'dan sequence senkronize et (sonraki çağrılar için)
    const respData = result.data as any;
    const respSeq = respData?.TransactionHandle?.TransactionSequence;
    if (respSeq && typeof respSeq === 'number') {
      const nextSeq = respSeq + 1;
      if (cachedSettings) cachedSettings.sequence = nextSeq;
      updateSequence(nextSeq).catch(() => {});
    }

    if (!result.success) {
      const errMsg = respData?.Message || 'Avans POS ödemesi başarısız';
      return { success: false, errorMessage: errMsg };
    }

    const saleData = respData?.Sale || respData?.Data || {};
    const saleNumber = saleData.SaleNumber || saleData.saleNumber || '';
    const invoiceNo = saleData.FinancialDocuments?.[0]?.InvoiceNo ||
                      saleData.InvoiceNo || saleData.invoiceNo || '';

    const payment = saleData.AddedPayments?.[0];
    const fiscalData: Record<string, any> = {
      saleNumber,
      orderNo,
      invoiceNo,
      saleId: saleData.Id,
      saleUid: saleData.SaleUid || '',
      statusId: saleData.StatusId,
      terminalId: saleData.TerminalId,
      terminalSerialNo: saleData.TerminalSerialNo,
      payments: saleData.AddedPayments?.map((p: any) => ({
        id: p.Id,
        mediatorId: p.PaymentMediatorId,
        amount: p.PaymentAmount,
        operationTypeId: p.OperationTypeId,
        cardNo: p.OnlinePayment?.CardNo,
        authorizationCode: p.OnlinePayment?.AuthorizationCode,
      })),
    };
    if (payment?.OnlinePayment) {
      const op = payment.OnlinePayment;
      fiscalData.cardNo = op.CardNo || '';
      fiscalData.authorizationCode = op.AuthorizationCode || '';
      fiscalData.transactionNo = op.TransactionNo || '';
      fiscalData.batchNo = op.BatchNo || '';
      fiscalData.acquirerName = op.AcquirerName || '';
      fiscalData.responseCode = op.ResponseCode || '';
    }

    console.log('[PavoPay] AdvanceSale başarılı:', { saleNumber, orderNo, invoiceNo });

    return {
      success: true,
      saleNumber,
      orderNo,
      invoiceNo,
      fiscalTransactionData: JSON.stringify(fiscalData),
    };
  } catch (error: any) {
    console.error('[PavoPay] AdvanceSale hata:', error);
    return { success: false, errorMessage: error.message || 'PavoPay bağlantı hatası' };
  }
};

// ============================================
// PARÇALI ÖDEME FONKSİYONLARI
// ============================================

/**
 * PavoPay parçalı ödeme sonucu
 */
export interface PavoPayPartialPaymentResult {
  success: boolean;
  saleNumber?: string;
  invoiceNo?: string;
  remainingAmount?: number;
  paidAmount?: number;
  totalAmount?: number;
  errorMessage?: string;
  data?: any;
}

/**
 * Parçalı ödeme için satışı başlat (StartSaleWithItems)
 * @param products Satılacak ürünler
 * @param totalAmount Toplam tutar
 * @param companyId Şirket ID
 * @param isVatIncluded Fiyatlar KDV dahil mi? (Restoran modu için true)
 */
export const startSaleWithItems = async (
  products: Array<{
    name: string;
    quantity: number;
    unitPrice: number;
    vatRate: number;
    departmentNo?: number;
  }>,
  totalAmount: number,
  companyId: string,
  isVatIncluded: boolean = false,
  kitchenOrderCode?: string,
  receiptCompanyName?: string,
  useExternalPrinter?: boolean
): Promise<PavoPayPartialPaymentResult> => {
  try {
    console.log('[PavoPay] 🔄 StartSaleWithItems başlıyor...', { productCount: products.length, totalAmount, isVatIncluded });
    
    const settings = await getPavoPaySettings();
    if (!settings) {
      return { success: false, errorMessage: 'PavoPay ayarları bulunamadı' };
    }
    
    // Ürünleri PavoPay formatına dönüştür
    const addedSaleItems = products.map(product => {
      const vatRate = product.vatRate ?? 0;
      const taxGroupCode = vatRate === 20 ? 'KDV20' : 
                          vatRate === 10 ? 'KDV10' : 
                          vatRate === 8 ? 'KDV8' : 
                          vatRate === 1 ? 'KDV1' : 'KDV0';
      
      // ✅ FIX: Restoran modunda fiyatlar zaten KDV dahil
      let unitPriceWithVat: number;
      let itemTotalPriceWithVat: number;
      
      if (isVatIncluded) {
        // RESTORAN MODU: Fiyatlar zaten KDV dahil, olduğu gibi kullan
        unitPriceWithVat = product.unitPrice;
        itemTotalPriceWithVat = product.unitPrice * product.quantity;
      } else {
        // NORMAL HIZLI SATIŞ: Fiyatlar KDV hariç, üzerine KDV ekle
        unitPriceWithVat = product.unitPrice * (1 + (vatRate / 100));
        itemTotalPriceWithVat = unitPriceWithVat * product.quantity;
      }
      
      console.log('[PavoPay] 📊 Ürün Fiyat Dönüşümü:', {
        name: product.name,
        unitPrice: product.unitPrice,
        vatRate: vatRate,
        isVatIncluded,
        unitPriceWithVat: unitPriceWithVat.toFixed(2),
        quantity: product.quantity,
        totalWithVat: itemTotalPriceWithVat.toFixed(2)
      });
      
      return {
        Name: product.name,
        IsGeneric: false,
        UnitCode: 'C62', // Adet
        TaxGroupCode: taxGroupCode,
        ItemQuantity: product.quantity,
        UnitPriceAmount: parseFloat(unitPriceWithVat.toFixed(2)), // ✅ KDV DAHİL birim fiyat
        GrossPriceAmount: parseFloat(itemTotalPriceWithVat.toFixed(2)), // ✅ KDV DAHİL toplam
        TotalPriceAmount: parseFloat(itemTotalPriceWithVat.toFixed(2)) // ✅ KDV DAHİL toplam
      };
    });
    
    // ✅ DÜZELTME: GrossPrice ve TotalPrice AddedSaleItems toplamından hesaplanmalı!
    // pavopay-test.html: const total = basket.reduce((sum, item) => sum + item.TotalPrice, 0);
    const calculatedTotal = addedSaleItems.reduce((sum, item) => sum + item.TotalPriceAmount, 0);
    
    console.log('[PavoPay] 📊 Fiyat Kontrolü:', {
      paramTotalAmount: totalAmount,
      calculatedTotal,
      difference: Math.abs(totalAmount - calculatedTotal)
    });
    
    const { top: topPrintableItems, bottom: bottomPrintableItems } =
      buildKitchenPrintableItems(kitchenOrderCode, receiptCompanyName);

    const saleRequest = {
      TransactionHandle: createTransactionHandle(settings),
      Sale: {
        RefererApp: 'GTS Yazılım',
        RefererAppVersion: '1.0.0',
        MainDocumentType: getMainDocumentType(), // 1=E-Fatura, 2=E-Arşiv
        GrossPrice: calculatedTotal,
        TotalPrice: calculatedTotal,
        SendPhoneNotification: false,
        SendEMailNotification: false,
        AskCustomer: false,
        ...(topPrintableItems && { TopPrintableItems: topPrintableItems, BottomPrintableItems: bottomPrintableItems }),
        AddedSaleItems: addedSaleItems,
        ReceiptInformation: {
          PrintCustomerReceipt: true,
          PrintCustomerReceiptCopy: false,
          PrintMerchantReceipt: true,
          ReceiptWidth: useExternalPrinter ? '80mm' : '58mm',
        },
      }
    };
    
    console.log('[PavoPay] StartSaleWithItems Request:', JSON.stringify(saleRequest, null, 2));
    
    const response = await fetchWithFallback(`${getLocalServiceApiBaseUrl()}/pavopay-start-sale`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        posIp: settings.posIp,
        port: settings.port,
        saleRequest
      })
    });
    
    if (!response.ok) {
      return { success: false, errorMessage: `HTTP ${response.status}` };
    }
    
    const result = await response.json();
    console.log('[PavoPay] StartSaleWithItems Response:', result);
    
    const hasError = result.data?.HasError === true;
    const saleData = result.data?.Data || result.data || {};
    const saleNumber = saleData.SaleNumber || saleData.saleNumber;
    
    if (!hasError && saleNumber) {
      console.log('[PavoPay] ✅ Satış başlatıldı:', saleNumber);
      return {
        success: true,
        saleNumber,
        remainingAmount: calculatedTotal,
        paidAmount: 0,
        totalAmount: calculatedTotal,
        data: result.data
      };
    } else {
      const errorMessage = result.data?.Message || result.message || 'Satış başlatılamadı';
      console.error('[PavoPay] ❌ StartSaleWithItems hatası:', errorMessage);
      return { success: false, errorMessage };
    }
    
  } catch (error: any) {
    console.error('[PavoPay] StartSaleWithItems genel hatası:', error);
    return { success: false, errorMessage: error.message || 'Bağlantı hatası' };
  }
};

/**
 * Parçalı ödeme ekle (AddPayment)
 * @param saleNumber Satış numarası
 * @param amount Ödeme tutarı
 * @param mediator Ödeme yöntemi (1=Nakit, 2=Kredi Kartı)
 * @param totalAmount Toplam satış tutarı
 */
export const addPaymentToSale = async (
  saleNumber: string,
  amount: number,
  mediator: number,
  totalAmount: number
): Promise<PavoPayPartialPaymentResult> => {
  try {
    console.log('[PavoPay] 🔄 AddPayment başlıyor...', { saleNumber, amount, mediator });
    
    const settings = await getPavoPaySettings();
    if (!settings) {
      return { success: false, errorMessage: 'PavoPay ayarları bulunamadı' };
    }
    
    const paymentRequest = {
      TransactionHandle: createTransactionHandle(settings),
      Sale: {
        SaleNumber: saleNumber,
        GrossPrice: totalAmount,
        SkipAmountCash: mediator === 1,
        ShowCreditCardMenu: false,
        AskCustomer: false,
        SelectedSlots: ['rf', 'icc', 'manual', 'qr', 'magneticStripe'],
        CardReadTimeout: 40,
        CancelPaymentLater: true,
        TryAgainOnPaymentFailure: false,
        PaymentInformations: [
          {
            Mediator: mediator,
            Amount: amount,
            CurrencyCode: 'TRY',
            ExchangeRate: 1
          }
        ],
        AllowedPaymentMediators: [{ Mediator: mediator }]
      }
    };
    
    console.log('[PavoPay] AddPayment Request:', JSON.stringify(paymentRequest, null, 2));
    
    const response = await fetchWithFallback(`${getLocalServiceApiBaseUrl()}/pavopay-add-payment`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        posIp: settings.posIp,
        port: settings.port,
        paymentRequest
      })
    });
    
    if (!response.ok) {
      return { success: false, errorMessage: `HTTP ${response.status}` };
    }
    
    const result = await response.json();
    console.log('[PavoPay] AddPayment Response:', result);
    
    const hasError = result.data?.HasError === true;
    const errorCode = result.data?.ErrorCode || '';
    const errorMessage = result.data?.Message || result.message || '';
    const paymentData = result.data?.Data || result.data || {};
    
    // Ödeme başarılı mı kontrol et
    const paymentStatusId = paymentData.StatusId || paymentData.statusId;
    const addedPayments = paymentData.AddedPayments || [];

    // ✅ DÜZELTME: hasCompletedPayment KESİN kontrol — yeni eklenen parça
    // gerçekten Completed mı? (eski kodda `result.success && !hasError` gevşek
    // kriteri iptal durumlarında bile true dönebiliyordu → masa yanlış kapatılıyordu)
    const hasCompletedPayment = addedPayments.some((p: any) => 
      p.StatusId === 2 || p.statusId === 2 || // 2 = Completed
      p.ResponseCode === "00" || p.responseCode === "00" // 00 = Başarılı
    );

    // StatusId haritası: 1=Aktif, 2=Tamamlandı, 7=İptal, 8=Red, 9=Timeout
    const cancelledStatusIds = [7, 8, 9];
    const isCancelledByStatus =
      paymentStatusId !== undefined && cancelledStatusIds.includes(paymentStatusId);

    // ErrorCode üzerinden ödeme başarısızlığı
    const paymentFailureErrors = ['PaymentCancelled', 'UserCancelled', 'Timeout', 'CardReadTimeout', 'PaymentFailed', 'Cancelled', 'Rejected'];
    const isPaymentFailed = paymentFailureErrors.some(err => typeof errorCode === 'string' && errorCode.includes(err));

    // ✅ YENİ BAŞARI KRİTERİ — TÜM ŞARTLAR SAĞLANMALI:
    // 1. Cihaz hatası yok (!hasError)
    // 2. StatusId iptal/red/timeout değil (!isCancelledByStatus)
    // 3. ErrorCode iptal/red/timeout içermiyor (!isPaymentFailed)
    // 4. Ya StatusId=2 ya da AddedPayments içinde Completed bir kayıt var
    const isPaymentCompleted =
      !hasError &&
      !isCancelledByStatus &&
      !isPaymentFailed &&
      (paymentStatusId === 2 || hasCompletedPayment);

    console.log('[PavoPay] 🔍 AddPayment Detaylı Durum:', {
      hasError,
      errorCode,
      paymentStatusId,
      addedPaymentsCount: addedPayments.length,
      hasCompletedPayment,
      isCancelledByStatus,
      isPaymentFailed,
      '→ isPaymentCompleted': isPaymentCompleted,
    });
    
    if (isPaymentCompleted) {
      // Kalan tutarı hesapla
      const remainingPayment = paymentData.RemainingPaymentAmount ?? 
        (totalAmount - addedPayments.reduce((sum: number, p: any) => sum + (p.PaymentAmount || 0), 0));
      
      console.log('[PavoPay] ✅ Ödeme eklendi:', { amount, remaining: remainingPayment });
      return {
        success: true,
        saleNumber,
        paidAmount: amount,
        remainingAmount: remainingPayment,
        totalAmount,
        data: result.data
      };
    } else {
      // Kullanıcıya gösterilecek anlamlı hata mesajı üret
      let userMessage: string;
      if (isCancelledByStatus) {
        userMessage = paymentStatusId === 7 ? 'POS işlemi iptal edildi'
          : paymentStatusId === 8 ? 'POS işlemi reddedildi'
          : 'POS işlemi zaman aşımına uğradı';
      } else if (isPaymentFailed) {
        userMessage = `Ödeme başarısız: ${errorCode}`;
      } else if (!hasCompletedPayment && addedPayments.length === 0) {
        userMessage = 'Kart okutulmadı veya ödeme başlatılmadı';
      } else if (!hasCompletedPayment) {
        userMessage = 'Ödeme tamamlanmadı';
      } else {
        userMessage = errorMessage || 'Ödeme eklenemedi';
      }
      console.error('[PavoPay] ❌ AddPayment hatası:', userMessage, { errorCode, paymentStatusId });
      return { success: false, errorMessage: userMessage };
    }
    
  } catch (error: any) {
    console.error('[PavoPay] AddPayment genel hatası:', error);
    return { success: false, errorMessage: error.message || 'Bağlantı hatası' };
  }
};

/**
 * Satışı tamamla (FinalizeSale)
 * @param saleNumber Satış numarası
 * @param companyId Şirket ID (satış kaydı için)
 * @param totalAmount Toplam tutar
 * @param products Ürün listesi (satış kaydı için)
 */
export const finalizePavoPaySale = async (
  saleNumber: string,
  companyId: string,
  totalAmount: number,
  products?: Array<{ name: string; quantity: number; price: number }>,
  kitchenOrderCode?: string,
  receiptCompanyName?: string,
  useExternalPrinter?: boolean
): Promise<PavoPayPartialPaymentResult> => {
  try {
    console.log('[PavoPay] 🔄 FinalizeSale başlıyor...', { saleNumber, kitchenOrderCode, useExternalPrinter });
    
    const settings = await getPavoPaySettings();
    if (!settings) {
      return { success: false, errorMessage: 'PavoPay ayarları bulunamadı' };
    }

    const { top: topPrintableItems, bottom: bottomPrintableItems } =
      buildKitchenPrintableItems(kitchenOrderCode, receiptCompanyName);
    
    const finalizeRequest = {
      TransactionHandle: createTransactionHandle(settings),
      Sale: {
        SaleNumber: saleNumber,
        ...(topPrintableItems && { TopPrintableItems: topPrintableItems, BottomPrintableItems: bottomPrintableItems }),
        ReceiptInformation: {
          PrintCustomerReceipt: true,
          PrintCustomerReceiptCopy: false,
          PrintMerchantReceipt: true,
          ReceiptWidth: useExternalPrinter ? '80mm' : '58mm',
        },
      }
    };
    
    console.log('[PavoPay] FinalizeSale Request:', JSON.stringify(finalizeRequest, null, 2));
    
    const response = await fetchWithFallback(`${getLocalServiceApiBaseUrl()}/pavopay-finalize-sale`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        posIp: settings.posIp,
        port: settings.port,
        finalizeRequest
      })
    });
    
    if (!response.ok) {
      return { success: false, errorMessage: `HTTP ${response.status}` };
    }
    
    const result = await response.json();
    console.log('[PavoPay] FinalizeSale Response:', result);
    
    const hasError = result.data?.HasError === true;
    const errorCode = result.data?.ErrorCode || '';
    const saleData = result.data?.Data || result.data || {};
    const invoiceNo = saleData.InvoiceNo || saleData.invoiceNo;

    // ✅ DÜZELTME: sadece !hasError yetmiyor — StatusId + AddedPayments kontrolü
    // ekleyerek cihazda aslında ödeme alınmadan finalize edilen satışların
    // yanlış başarılı sayılmasını engelle.
    const saleStatusId = saleData.StatusId || saleData.statusId;
    const addedPayments = saleData.AddedPayments || [];
    const hasCompletedPayment = addedPayments.some((p: any) =>
      p.StatusId === 2 || p.statusId === 2 ||
      p.ResponseCode === "00" || p.responseCode === "00"
    );
    const cancelledStatusIds = [7, 8, 9];
    const isCancelledByStatus =
      saleStatusId !== undefined && cancelledStatusIds.includes(saleStatusId);

    // StatusId=2 (Tamamlandı) ya da en az bir Completed ödeme olmalı.
    // AddedPayments hiç dönmediyse (bazı FinalizeSale şemalarında olmayabilir)
    // ve StatusId de verilmediyse, geriye dönük uyumluluk için sadece !hasError
    // şartına güveniyoruz (yanlış pozitif riskini StatusId/iptal kontrolü ile azaltıyoruz).
    const hasAnyPaymentInfo = addedPayments.length > 0 || saleStatusId !== undefined;
    const isSuccess =
      !hasError &&
      !isCancelledByStatus &&
      (hasAnyPaymentInfo
        ? (saleStatusId === 2 || hasCompletedPayment)
        : true);

    console.log('[PavoPay] 🔍 FinalizeSale Detaylı Durum:', {
      hasError,
      errorCode,
      saleStatusId,
      addedPaymentsCount: addedPayments.length,
      hasCompletedPayment,
      isCancelledByStatus,
      hasAnyPaymentInfo,
      '→ isSuccess': isSuccess,
    });

    if (isSuccess) {
      console.log('[PavoPay] ✅ Satış tamamlandı:', saleNumber);
      
      // Satışı kaydet
      savePavoPaySale({
        saleNumber,
        invoiceNo: invoiceNo || '',
        amount: totalAmount,
        date: new Date().toISOString(),
        paymentType: 'KARMA',
        items: products,
        fiscalData: result.data
      }, companyId);
      
      return {
        success: true,
        saleNumber,
        invoiceNo,
        totalAmount,
        data: result.data
      };
    } else {
      let userMessage: string;
      if (isCancelledByStatus) {
        userMessage = saleStatusId === 7 ? 'Satış iptal edildi'
          : saleStatusId === 8 ? 'Ödeme reddedildi'
          : 'İşlem zaman aşımına uğradı';
      } else if (hasAnyPaymentInfo && !hasCompletedPayment && saleStatusId !== 2) {
        userMessage = 'Satış tamamlanmadı — ödeme alınamamış olabilir';
      } else {
        userMessage = result.data?.Message || result.message || 'Satış tamamlanamadı';
      }
      console.error('[PavoPay] ❌ FinalizeSale hatası:', userMessage, { errorCode, saleStatusId });
      return { success: false, errorMessage: userMessage };
    }
    
  } catch (error: any) {
    console.error('[PavoPay] FinalizeSale genel hatası:', error);
    return { success: false, errorMessage: error.message || 'Bağlantı hatası' };
  }
};

/**
 * Tek seferde ödeme ekle ve tamamla (AddPaymentAndFinalizeSale)
 * Önceden StartSaleWithItems ile başlatılmış satış için kullanılır
 */
export const addPaymentAndFinalizeSale = async (
  saleNumber: string,
  amount: number,
  mediator: number,
  companyId: string,
  totalAmount: number,
  products?: Array<{ name: string; quantity: number; price: number }>,
  kitchenOrderCode?: string,
  receiptCompanyName?: string,
  useExternalPrinter?: boolean
): Promise<PavoPayPartialPaymentResult> => {
  try {
    console.log('[PavoPay] 🔄 AddPaymentAndFinalizeSale başlıyor...', { saleNumber, amount, mediator, kitchenOrderCode, useExternalPrinter });
    
    const settings = await getPavoPaySettings();
    if (!settings) {
      return { success: false, errorMessage: 'PavoPay ayarları bulunamadı' };
    }

    const { top: topPrintableItems, bottom: bottomPrintableItems } =
      buildKitchenPrintableItems(kitchenOrderCode, receiptCompanyName);
    
    const saleRequest = {
      TransactionHandle: createTransactionHandle(settings),
      Sale: {
        SaleNumber: saleNumber,
        GrossPrice: totalAmount,
        SkipAmountCash: mediator === 1,
        ShowCreditCardMenu: false,
        AskCustomer: false,
        SelectedSlots: ['rf', 'icc', 'manual', 'qr', 'magneticStripe'],
        CardReadTimeout: 40,
        CancelPaymentLater: true,
        TryAgainOnPaymentFailure: false,
        ...(topPrintableItems && { TopPrintableItems: topPrintableItems, BottomPrintableItems: bottomPrintableItems }),
        ReceiptInformation: {
          PrintCustomerReceipt: true,
          PrintCustomerReceiptCopy: false,
          PrintMerchantReceipt: true,
          ReceiptWidth: useExternalPrinter ? '80mm' : '58mm',
        },
        PaymentInformations: [
          {
            Mediator: mediator,
            Amount: amount,
            CurrencyCode: 'TRY',
            ExchangeRate: 1
          }
        ],
        AllowedPaymentMediators: [{ Mediator: mediator }]
      }
    };
    
    console.log('[PavoPay] AddPaymentAndFinalizeSale Request:', JSON.stringify(saleRequest, null, 2));
    
    const response = await fetchWithFallback(`${getLocalServiceApiBaseUrl()}/pavopay-add-payment-finalize`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        posIp: settings.posIp,
        port: settings.port,
        saleRequest
      })
    });
    
    if (!response.ok) {
      return { success: false, errorMessage: `HTTP ${response.status}` };
    }
    
    const result = await response.json();
    console.log('[PavoPay] AddPaymentAndFinalizeSale Response:', result);
    
    const hasError = result.data?.HasError === true;
    const errorCode = result.data?.ErrorCode || '';
    const saleData = result.data?.Data || result.data || {};
    const invoiceNo = saleData.InvoiceNo || saleData.invoiceNo;
    
    // Ödeme kontrolü
    const addedPayments = saleData.AddedPayments || [];
    const hasCompletedPayment = addedPayments.some((p: any) => 
      p.StatusId === 2 || p.statusId === 2 ||
      p.ResponseCode === "00" || p.responseCode === "00"
    );

    // ✅ DÜZELTME: iptal/red/timeout StatusId kontrolü ekle (7=İptal, 8=Red, 9=Timeout)
    const saleStatusId = saleData.StatusId || saleData.statusId;
    const cancelledStatusIds = [7, 8, 9];
    const isCancelledByStatus =
      saleStatusId !== undefined && cancelledStatusIds.includes(saleStatusId);
    const paymentFailureErrors = ['PaymentCancelled', 'UserCancelled', 'Timeout', 'CardReadTimeout', 'PaymentFailed', 'Cancelled', 'Rejected'];
    const isPaymentFailed = paymentFailureErrors.some(err => typeof errorCode === 'string' && errorCode.includes(err));

    const isSuccess = !hasError && !isCancelledByStatus && !isPaymentFailed && hasCompletedPayment;

    console.log('[PavoPay] 🔍 AddPaymentAndFinalizeSale Detaylı Durum:', {
      hasError,
      errorCode,
      saleStatusId,
      addedPaymentsCount: addedPayments.length,
      hasCompletedPayment,
      isCancelledByStatus,
      isPaymentFailed,
      '→ isSuccess': isSuccess,
    });

    if (isSuccess) {
      console.log('[PavoPay] ✅ Ödeme + Satış tamamlandı:', saleNumber);
      
      // Satışı kaydet
      savePavoPaySale({
        saleNumber,
        invoiceNo: invoiceNo || '',
        amount: totalAmount,
        date: new Date().toISOString(),
        paymentType: 'KARMA',
        items: products,
        fiscalData: result.data
      }, companyId);
      
      return {
        success: true,
        saleNumber,
        invoiceNo,
        totalAmount,
        paidAmount: amount,
        remainingAmount: 0,
        data: result.data
      };
    } else {
      let userMessage: string;
      if (isCancelledByStatus) {
        userMessage = saleStatusId === 7 ? 'Satış iptal edildi'
          : saleStatusId === 8 ? 'Ödeme reddedildi'
          : 'İşlem zaman aşımına uğradı';
      } else if (isPaymentFailed) {
        userMessage = `Ödeme başarısız: ${errorCode}`;
      } else if (!hasCompletedPayment) {
        userMessage = 'Kart okutulmadı veya ödeme tamamlanmadı';
      } else {
        userMessage = result.data?.Message || result.message || 'Ödeme/Satış tamamlanamadı';
      }
      console.error('[PavoPay] ❌ AddPaymentAndFinalizeSale hatası:', userMessage, { errorCode, saleStatusId });
      return { success: false, errorMessage: userMessage };
    }
    
  } catch (error: any) {
    console.error('[PavoPay] AddPaymentAndFinalizeSale genel hatası:', error);
    return { success: false, errorMessage: error.message || 'Bağlantı hatası' };
  }
};

/**
 * Satıştan vazgeç (AbandonSuspendedSale)
 * @param identifier Satış numarası (SaleNumber) veya Sipariş numarası (OrderNo)
 * @param useOrderNo true ise OrderNo olarak kullan, false ise SaleNumber olarak kullan
 */
export const abandonPavoPaySale = async (
  identifier: string,
  useOrderNo: boolean = false
): Promise<{ success: boolean; errorMessage?: string }> => {
  try {
    const idType = useOrderNo ? 'OrderNo' : 'SaleNumber';
    console.log(`[PavoPay] 🔄 AbandonSuspendedSale başlıyor... (${idType}: ${identifier})`);
    
    const settings = await getPavoPaySettings();
    if (!settings) {
      return { success: false, errorMessage: 'PavoPay ayarları bulunamadı' };
    }
    
    // ✅ YENİ: OrderNo veya SaleNumber ile iptal et
    const saleData = useOrderNo 
      ? { OrderNo: identifier }
      : { SaleNumber: identifier };
    
    const abandonRequest = {
      TransactionHandle: createTransactionHandle(settings),
      Sale: saleData
    };
    
    const response = await fetchWithFallback(`${getLocalServiceApiBaseUrl()}/pavopay-abandon-sale`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        posIp: settings.posIp,
        port: settings.port,
        abandonRequest
      })
    });
    
    if (!response.ok) {
      return { success: false, errorMessage: `HTTP ${response.status}` };
    }
    
    const result = await response.json();
    console.log('[PavoPay] AbandonSuspendedSale Response:', result);
    
    if (result.success) {
      console.log('[PavoPay] ✅ Satıştan vazgeçildi:', identifier);
      return { success: true };
    } else {
      const errorMessage = result.data?.Message || result.message || 'Satıştan vazgeçilemedi';
      console.error('[PavoPay] ❌ AbandonSuspendedSale hatası:', errorMessage);
      return { success: false, errorMessage };
    }
    
  } catch (error: any) {
    console.error('[PavoPay] AbandonSuspendedSale genel hatası:', error);
    return { success: false, errorMessage: error.message || 'Bağlantı hatası' };
  }
};

/**
 * PavoPay GetSaleResult Response Interface
 */
export interface PavoPaySaleResultResponse {
  success: boolean;
  statusId?: number;
  saleNumber?: string;
  isOffline?: boolean;
  totalPrice?: number;
  invoiceNo?: string;
  ettn?: string;
  addedPayments?: any[];
  errorMessage?: string;
  rawData?: any;
}

/**
 * Satış durumunu sorgula (GetSaleResult)
 * Timeout sonrası veya askıda kalan satışları kontrol etmek için kullanılır
 * 
 * StatusId Değerleri:
 * - 1: Aktif (devam ediyor)
 * - 2: Tamamlandı (başarılı)
 * - 6: Beklemede
 * - 7: İptal edildi
 * - 8: Reddedildi
 * - 9: Timeout
 * 
 * @param saleNumber Satış numarası
 */
export const getSaleResult = async (
  saleNumber: string
): Promise<PavoPaySaleResultResponse> => {
  try {
    console.log('[PavoPay] 🔍 GetSaleResult başlıyor...', { saleNumber });
    
    const settings = await getPavoPaySettings();
    if (!settings) {
      return { success: false, errorMessage: 'PavoPay ayarları bulunamadı' };
    }
    
    const saleResultRequest = {
      TransactionHandle: createTransactionHandle(settings),
      Sale: {
        SaleNumber: saleNumber
      }
    };
    
    console.log('[PavoPay] GetSaleResult Request:', JSON.stringify(saleResultRequest, null, 2));
    
    const response = await fetchWithFallback(`${getLocalServiceApiBaseUrl()}/pavopay-get-sale-result`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        posIp: settings.posIp,
        port: settings.port,
        saleResultRequest
      })
    });
    
    if (!response.ok) {
      return { success: false, errorMessage: `HTTP ${response.status}` };
    }
    
    const result = await response.json();
    console.log('[PavoPay] GetSaleResult Response:', result);
    
    if (!result.success || result.data?.HasError) {
      const errorMessage = result.data?.Message || result.message || 'Satış durumu alınamadı';
      console.error('[PavoPay] ❌ GetSaleResult hatası:', errorMessage);
      return { success: false, errorMessage };
    }
    
    // Response'dan verileri çıkar
    const saleData = result.data?.Data || result.data || {};
    const statusId = saleData.StatusId || saleData.statusId;
    const invoiceNo = saleData.FinancialDocuments?.[0]?.InvoiceNo || 
                      saleData.InvoiceNo || 
                      saleData.invoiceNo || '';
    const ettn = extractETTNFromResponse(saleData);
    const addedPayments = saleData.AddedPayments || saleData.addedPayments || [];
    
    console.log('[PavoPay] ✅ GetSaleResult başarılı:', {
      saleNumber: saleData.SaleNumber || saleNumber,
      statusId,
      totalPrice: saleData.TotalPrice,
      invoiceNo,
      ettn,
      paymentCount: addedPayments.length
    });
    
    return {
      success: true,
      statusId,
      saleNumber: saleData.SaleNumber || saleNumber,
      isOffline: saleData.IsOffline || false,
      totalPrice: saleData.TotalPrice,
      invoiceNo,
      ettn,
      addedPayments,
      rawData: saleData
    };
    
  } catch (error: any) {
    console.error('[PavoPay] GetSaleResult genel hatası:', error);
    return { success: false, errorMessage: error.message || 'Bağlantı hatası' };
  }
};

/**
 * OrderNo ile satış durumunu sorgula (GetSaleResult)
 * Timeout sonrası satışı kurtarmak veya iptal etmek için kullanılır
 * 
 * @param orderNo Sipariş numarası (bizim oluşturduğumuz)
 */
export const getSaleResultByOrderNo = async (
  orderNo: string
): Promise<PavoPaySaleResultResponse> => {
  try {
    console.log('[PavoPay] 🔍 GetSaleResult (OrderNo) başlıyor...', { orderNo });
    
    const settings = await getPavoPaySettings();
    if (!settings) {
      return { success: false, errorMessage: 'PavoPay ayarları bulunamadı' };
    }
    
    const saleResultRequest = {
      TransactionHandle: createTransactionHandle(settings),
      Sale: {
        OrderNo: orderNo // ✅ SaleNumber yerine OrderNo kullan
      }
    };
    
    console.log('[PavoPay] GetSaleResult (OrderNo) Request:', JSON.stringify(saleResultRequest, null, 2));
    
    const response = await fetchWithFallback(`${getLocalServiceApiBaseUrl()}/pavopay-get-sale-result`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        posIp: settings.posIp,
        port: settings.port,
        saleResultRequest
      })
    });
    
    if (!response.ok) {
      return { success: false, errorMessage: `HTTP ${response.status}` };
    }
    
    const result = await response.json();
    console.log('[PavoPay] GetSaleResult (OrderNo) Response:', result);
    
    // ✅ YENİ: "İşlem devam ediyor" mesajını özel olarak ele al
    const responseMessage = result.data?.Message || result.message || '';
    const isInProgress = responseMessage.includes('devam etmektedir') || 
                         responseMessage.includes('in progress') ||
                         responseMessage.includes('InProgress');
    
    if (isInProgress) {
      console.log('[PavoPay] ⏳ İşlem cihazda devam ediyor, iptal edilecek...');
      return { 
        success: true, 
        statusId: 1, // 1 = Aktif (devam ediyor) - iptal edilecek
        saleNumber: orderNo, // OrderNo'yu SaleNumber olarak kullan (iptal için)
        errorMessage: 'İşlem cihazda devam ediyor'
      };
    }
    
    if (!result.success || result.data?.HasError) {
      const errorMessage = result.data?.Message || result.message || 'Satış durumu alınamadı';
      console.error('[PavoPay] ❌ GetSaleResult (OrderNo) hatası:', errorMessage);
      return { success: false, errorMessage };
    }
    
    // Response'dan verileri çıkar
    const saleData = result.data?.Data || result.data || {};
    const statusId = saleData.StatusId || saleData.statusId;
    const saleNumber = saleData.SaleNumber || saleData.saleNumber || '';
    const invoiceNo = saleData.FinancialDocuments?.[0]?.InvoiceNo || 
                      saleData.InvoiceNo || 
                      saleData.invoiceNo || '';
    const ettn = extractETTNFromResponse(saleData);
    const addedPayments = saleData.AddedPayments || saleData.addedPayments || [];
    
    console.log('[PavoPay] ✅ GetSaleResult (OrderNo) başarılı:', {
      orderNo,
      saleNumber,
      statusId,
      totalPrice: saleData.TotalPrice,
      invoiceNo,
      ettn,
      paymentCount: addedPayments.length
    });
    
    return {
      success: true,
      statusId,
      saleNumber,
      isOffline: saleData.IsOffline || false,
      totalPrice: saleData.TotalPrice,
      invoiceNo,
      ettn,
      addedPayments,
      rawData: saleData
    };
    
  } catch (error: any) {
    console.error('[PavoPay] GetSaleResult (OrderNo) genel hatası:', error);
    return { success: false, errorMessage: error.message || 'Bağlantı hatası' };
  }
};

/**
 * Timeout sonrası satış durumunu kontrol et ve gerekirse iptal et
 * 
 * Bu fonksiyon şu akışı izler:
 * 1. GetSaleResult ile satış durumunu sorgula
 * 2. StatusId = 2 (Tamamlandı) ise → Ödeme başarılı, true döndür
 * 3. Diğer durumlar (1, 6, 7, 8, 9) → AbandonSuspendedSale ile iptal et, false döndür
 * 
 * @param saleNumber Satış numarası
 * @param companyId Şirket ID (başarılı satış kaydı için)
 * @param totalAmount Toplam tutar (satış kaydı için)
 * @param products Ürün listesi (satış kaydı için)
 */
export const checkAndHandleSaleStatus = async (
  saleNumber: string,
  companyId?: string,
  totalAmount?: number,
  products?: Array<{ name: string; quantity: number; price: number }>
): Promise<{
  success: boolean;
  isCompleted: boolean;
  statusId?: number;
  invoiceNo?: string;
  errorMessage?: string;
}> => {
  try {
    console.log('[PavoPay] 🔄 checkAndHandleSaleStatus başlıyor...', { saleNumber });
    
    // 1. Satış durumunu sorgula
    const saleResult = await getSaleResult(saleNumber);
    
    if (!saleResult.success) {
      console.error('[PavoPay] ❌ Satış durumu alınamadı:', saleResult.errorMessage);
      return {
        success: false,
        isCompleted: false,
        errorMessage: saleResult.errorMessage
      };
    }
    
    const statusId = saleResult.statusId;
    console.log('[PavoPay] 📊 Satış durumu:', { statusId, saleNumber });
    
    // 2. StatusId = 2 (Tamamlandı) → Ödeme başarılı
    if (statusId === 2) {
      console.log('[PavoPay] ✅ Satış tamamlanmış (StatusId: 2)');
      
      // Başarılı satışı kaydet
      if (companyId && totalAmount) {
        savePavoPaySale({
          saleNumber,
          invoiceNo: saleResult.invoiceNo || '',
          amount: totalAmount,
          date: new Date().toISOString(),
          paymentType: 'POS',
          items: products,
          fiscalData: saleResult.rawData
        }, companyId);
      }
      
      return {
        success: true,
        isCompleted: true,
        statusId: 2,
        invoiceNo: saleResult.invoiceNo
      };
    }
    
    // 3. Diğer durumlar → Satış tamamlanmamış, iptal et
    console.log('[PavoPay] ⚠️ Satış tamamlanmamış (StatusId:', statusId, ')');
    
    // Satışı iptal et
    const abandonResult = await abandonPavoPaySale(saleNumber);
    
    if (abandonResult.success) {
      console.log('[PavoPay] ✅ Askıdaki satış iptal edildi');
    } else {
      console.warn('[PavoPay] ⚠️ Satış iptal edilemedi:', abandonResult.errorMessage);
    }
    
    // Status mesajlarını hazırla
    const statusMessages: { [key: number]: string } = {
      1: 'Satış hala aktif durumda',
      6: 'Satış beklemede',
      7: 'Satış iptal edildi',
      8: 'Satış reddedildi',
      9: 'Satış zaman aşımına uğradı'
    };
    
    const statusMessage = statusMessages[statusId || 0] || `Bilinmeyen durum (StatusId: ${statusId})`;
    
    return {
      success: true,
      isCompleted: false,
      statusId,
      errorMessage: statusMessage
    };
    
  } catch (error: any) {
    console.error('[PavoPay] checkAndHandleSaleStatus genel hatası:', error);
    return {
      success: false,
      isCompleted: false,
      errorMessage: error.message || 'Bağlantı hatası'
    };
  }
};

// ============================================
// AVANS SATIŞ İADE (GetReturnableSale + PartialReturnForAdvanceSale)
// ============================================

export interface ReturnableSalePayment {
  id: number;
  mediatorId: number;
  amount: number;
  remainingVoidableAmount: number;
  cardNo?: string;
  authorizationCode?: string;
  /** Satışı işleyen bankanın Pavo acquirer id'si (OnlinePayment.AcquirerId).
   *  Farklı cihazdan iptalde SelectedTerminals eşleştirmesi için kullanılır. */
  acquirerId?: string;
  acquirerName?: string;
}

export interface ReturnableSaleItem {
  id: number;
  name: string;
  itemQuantity: number;
  unitPriceAmount: number;
  grossPriceAmount: number;
  totalPriceAmount: number;
  vatRate: number;
}

export interface ReturnableSaleResult {
  success: boolean;
  saleId: number;
  saleNumber: string;
  grossPrice: number;
  payments: ReturnableSalePayment[];
  saleItems?: ReturnableSaleItem[];
  mediatorRefundableAmount: Record<string, number>;
  errorMessage?: string;
}

export const getReturnableSale = async (saleId: number, saleNumber?: string, saleUid?: string): Promise<ReturnableSaleResult> => {
  // ✅ F5: Rest-P2P aktifse Pavo cihazindan direkt sorgula
  const restCfg = getRestP2PConfig();
  if (restCfg.enabled && restCfg.deviceCode) {
    try {
      console.log('[PavoPay→RestP2P] GetReturnableSale:', { saleId, saleNumber, saleUid });
      const outcome = await restP2PPaymentService.requestGetReturnableSale({
        deviceCode: restCfg.deviceCode,
        saleId: saleId || null,
        saleNumber: saleNumber || null,
        saleUid: saleUid || null,
        companyId: restCfg.companyId,
        branchId: restCfg.branchId,
      });

      if (outcome.status !== 'completed') {
        return { success: false, saleId: 0, saleNumber: '', grossPrice: 0, payments: [], mediatorRefundableAmount: {}, errorMessage: outcome.message };
      }
      const r = outcome.result || {};
      // ⚠️ Rest-P2P cihaz cevabı bazen PascalCase döner (Id/PaymentMediatorId).
      // camelCase okunamazsa eşlenikli iade için RelatedPaymentId 0 kalıp
      // eşleniksize düşüyordu — her iki casing'i de tolere et.
      const payments: ReturnableSalePayment[] = (r.payments || []).map((p: any) => ({
        id: p.id ?? p.Id ?? 0,
        mediatorId: p.mediatorId ?? p.PaymentMediatorId ?? p.mediator ?? 0,
        amount: p.amount ?? p.Amount ?? p.PaymentAmount ?? 0,
        remainingVoidableAmount: p.remainingVoidableAmount ?? p.RemainingVoidableAmount ?? p.amount ?? p.Amount ?? 0,
        cardNo: p.cardNo ?? p.CardNo,
        authorizationCode: p.authorizationCode ?? p.AuthorizationCode,
        acquirerId: (p.acquirerId ?? p.AcquirerId)?.toString(),
        acquirerName: p.acquirerName ?? p.AcquirerName,
      }));
      const saleItems: ReturnableSaleItem[] = (r.saleItems || []).map((it: any) => ({
        id: it.id ?? it.Id ?? 0,
        name: it.name ?? it.Name ?? '',
        itemQuantity: it.itemQuantity ?? it.ItemQuantity ?? 1,
        unitPriceAmount: it.unitPriceAmount ?? it.UnitPriceAmount ?? 0,
        grossPriceAmount: it.grossPriceAmount ?? it.GrossPriceAmount ?? it.totalPriceAmount ?? it.TotalPriceAmount ?? 0,
        totalPriceAmount: it.totalPriceAmount ?? it.TotalPriceAmount ?? 0,
        vatRate: it.vatRate ?? it.VatRate ?? 0,
      }));
      return {
        success: true,
        saleId: r.saleId ?? r.SaleId ?? 0,
        saleNumber: r.saleNumber ?? r.SaleNumber ?? saleNumber ?? '',
        grossPrice: r.grossPrice ?? r.GrossPrice ?? 0,
        payments,
        saleItems,
        mediatorRefundableAmount: r.mediatorRefundableAmount || r.MediatorRefundableAmount || {},
      };
    } catch (err: any) {
      return { success: false, saleId: 0, saleNumber: '', grossPrice: 0, payments: [], mediatorRefundableAmount: {}, errorMessage: err?.message || 'Rest-P2P sorgu hatası' };
    }
  }

  try {
    const settings = await getPavoPaySettings(true);
    if (!settings) {
      return { success: false, saleId: 0, saleNumber: '', grossPrice: 0, payments: [], mediatorRefundableAmount: {}, errorMessage: 'PavoPay ayarları bulunamadı.' };
    }

    console.log('[PavoPay] GetReturnableSale: Pairing ile cihaz senkronize ediliyor...');
    const pairingResult = await callPavoPayAPI('Pairing', {
      TransactionHandle: {
        SerialNumber: settings.serialNumber,
        Fingerprint: settings.fingerprint,
        TransactionSequence: 1,
        TransactionDate: new Date().toISOString()
      }
    });

    if (!pairingResult.success) {
      return { success: false, saleId: 0, saleNumber: '', grossPrice: 0, payments: [], mediatorRefundableAmount: {}, errorMessage: 'PavoPay cihazına bağlanılamadı.' };
    }

    const pairingData = pairingResult.data as any;
    const deviceSeq = pairingData?.TransactionHandle?.TransactionSequence;
    if (deviceSeq && typeof deviceSeq === 'number') {
      const syncedSeq = deviceSeq + 1;
      settings.sequence = syncedSeq;
      if (cachedSettings) cachedSettings.sequence = syncedSeq;
      updateSequence(syncedSeq).catch(() => {});
    }

    const handle = createTransactionHandle(settings);

    const saleObj: Record<string, any> = {};
    if (saleId && saleId !== 0) {
      saleObj.Id = saleId;
    }
    if (saleNumber) {
      saleObj.SaleNumber = saleNumber;
    }
    if (saleUid) {
      saleObj.SaleUid = saleUid;
    }
    if (Object.keys(saleObj).length === 0) {
      return { success: false, saleId: 0, saleNumber: '', grossPrice: 0, payments: [], mediatorRefundableAmount: {}, errorMessage: 'Satış tanımlayıcısı bulunamadı (saleId, saleNumber veya saleUid gerekli).' };
    }

    const saleRequest = {
      TransactionHandle: handle,
      Sale: saleObj
    };

    console.log('[PavoPay] GetReturnableSale request:', saleRequest);

    const result = await callPavoPayAPI('GetReturnableSale', saleRequest);

    const respData = result.data as any;
    const respSeq = respData?.TransactionHandle?.TransactionSequence;
    if (respSeq && typeof respSeq === 'number') {
      const nextSeq = respSeq + 1;
      if (cachedSettings) cachedSettings.sequence = nextSeq;
      updateSequence(nextSeq).catch(() => {});
    }

    if (!result.success) {
      return { success: false, saleId: 0, saleNumber: '', grossPrice: 0, payments: [], mediatorRefundableAmount: {}, errorMessage: respData?.Message || 'GetReturnableSale başarısız' };
    }

    const saleData = respData?.Sale || respData?.Data || {};

    const payments: ReturnableSalePayment[] = (saleData.AddedPayments || []).map((p: any) => ({
      id: p.Id,
      mediatorId: p.PaymentMediatorId,
      amount: p.PaymentAmount,
      remainingVoidableAmount: p.RemainingVoidableAmount || 0,
      cardNo: p.OnlinePayment?.CardNo || p.CardNo,
      authorizationCode: p.OnlinePayment?.AuthorizationCode || p.ReservedText,
      acquirerId: p.OnlinePayment?.AcquirerId?.toString(),
      acquirerName: p.OnlinePayment?.AcquirerName,
    }));

    console.log('[PavoPay] GetReturnableSale başarılı:', {
      saleId: saleData.Id,
      saleNumber: saleData.SaleNumber,
      payments: payments.length,
      mediatorRefundableAmount: saleData.MediatorRefundableAmount,
    });

    const saleItems: ReturnableSaleItem[] = (saleData.AddedSaleItems || []).map((item: any) => ({
      id: item.Id,
      name: item.Name || '',
      itemQuantity: item.ItemQuantity || 1,
      unitPriceAmount: item.UnitPriceAmount || 0,
      grossPriceAmount: item.GrossPriceAmount || 0,
      totalPriceAmount: item.TotalPriceAmount || 0,
      vatRate: item.VATRate || 0,
    }));

    return {
      success: true,
      saleId: saleData.Id,
      saleNumber: saleData.SaleNumber || '',
      grossPrice: saleData.GrossPrice || saleData.TotalPrice || 0,
      payments,
      saleItems,
      mediatorRefundableAmount: saleData.MediatorRefundableAmount || {},
    };
  } catch (error: any) {
    console.error('[PavoPay] GetReturnableSale hata:', error);
    return { success: false, saleId: 0, saleNumber: '', grossPrice: 0, payments: [], saleItems: [], mediatorRefundableAmount: {}, errorMessage: error.message || 'PavoPay bağlantı hatası' };
  }
};

// ============================================
// CİHAZ TERMİNAL LİSTESİ (GetDeviceInfo) — FARKLI CİHAZDAN İPTAL DESTEĞİ
// X cihazından yapılan POS satışı Y cihazından iptal edilirken, Pavo iptali
// doğru bankaya yönlendirebilsin diye Y cihazındaki banka/terminal tanımları
// (listTerminals) okunur ve CancelSale isteğine SelectedTerminals eklenir.
// NOT: Sadece REST (local-service) entegrasyonunda kullanılır; Rest-P2P için
// cihazdaki gts_pavo uygulamasının güncellenmesi gerekir.
// ============================================

export interface PavoDeviceTerminal {
  acquirerId: string;
  acquirerName: string;
  terminalLabel: string;
  terminalId: string;
}

export const getPavoDeviceTerminals = async (): Promise<{
  success: boolean;
  terminals: PavoDeviceTerminal[];
  errorMessage?: string;
}> => {
  try {
    const settings = await getPavoPaySettings();
    if (!settings) {
      return { success: false, terminals: [], errorMessage: 'PavoPay ayarları bulunamadı' };
    }

    const handle = createTransactionHandle(settings);
    const request = {
      TransactionHandle: handle,
      DeviceInfo: {
        AdditionalInfo: {
          listTerminals: true
        }
      }
    };

    const result = await callPavoPayAPI('GetDeviceInfo', request);

    const respData = result.data as any;
    const respSeq = respData?.TransactionHandle?.TransactionSequence;
    if (respSeq && typeof respSeq === 'number') {
      const nextSeq = respSeq + 1;
      if (cachedSettings) cachedSettings.sequence = nextSeq;
      updateSequence(nextSeq).catch(() => {});
    }

    if (!result.success) {
      return { success: false, terminals: [], errorMessage: respData?.Message || 'GetDeviceInfo başarısız' };
    }

    const list = respData?.Data?.listTerminals || respData?.Data?.ListTerminals || [];
    const terminals: PavoDeviceTerminal[] = (Array.isArray(list) ? list : [])
      .map((t: any) => ({
        acquirerId: (t.acquirerId ?? t.AcquirerId ?? '').toString(),
        acquirerName: t.acquirerName ?? t.AcquirerName ?? '',
        terminalLabel: t.terminalLabel ?? t.TerminalLabel ?? '',
        terminalId: (t.terminalId ?? t.TerminalId ?? '').toString(),
      }))
      .filter((t: PavoDeviceTerminal) => t.terminalId !== '');

    console.log('[PavoPay] GetDeviceInfo listTerminals:', terminals);
    return { success: true, terminals };
  } catch (error: any) {
    console.error('[PavoPay] GetDeviceInfo hata:', error);
    return { success: false, terminals: [], errorMessage: error.message || 'GetDeviceInfo bağlantı hatası' };
  }
};

/**
 * İptal edilecek satışın bankasını (GetReturnableSale → OnlinePayment.AcquirerId)
 * bulup, iptali YAPACAK cihazdaki aynı bankanın terminalId'sini döndürür.
 * CancelSale isteğinde `Sale.SelectedTerminals` olarak kullanılır.
 *
 * Herhangi bir adım çözülemezse null döner ve iptal, SelectedTerminals'sız
 * (mevcut davranış) devam eder — iptal akışını asla bloklamaz.
 */
const resolveCancelSelectedTerminals = async (saleNumber: string): Promise<string[] | null> => {
  try {
    // 1. Orijinal satışın banka bilgisi (satış hangi cihazdan yapılmış olursa olsun
    //    Pavo tarafında kayıtlı; bu cihazdan sorgulanabilir)
    const returnable = await getReturnableSale(0, saleNumber);
    if (!returnable.success) {
      console.warn(`[PavoPay][SelectedTerminals] GetReturnableSale başarısız (${returnable.errorMessage || '-'}), terminal eşleştirme atlanıyor.`);
      return null;
    }

    const acquirerIds = Array.from(new Set(
      (returnable.payments || [])
        .map(p => p.acquirerId)
        .filter((a): a is string => !!a && a !== '')
    ));

    if (acquirerIds.length === 0) {
      // Nakit ödeme veya banka bilgisi dönmemiş olabilir — eşleştirme yapılamaz.
      console.warn('[PavoPay][SelectedTerminals] Satış ödemelerinde AcquirerId bulunamadı, terminal eşleştirme atlanıyor.');
      return null;
    }

    // 2. Bu (iptali yapan) cihazdaki banka/terminal tanımları
    const deviceInfo = await getPavoDeviceTerminals();
    if (!deviceInfo.success || deviceInfo.terminals.length === 0) {
      console.warn(`[PavoPay][SelectedTerminals] Cihaz terminal listesi alınamadı (${deviceInfo.errorMessage || 'liste boş'}), terminal eşleştirme atlanıyor.`);
      return null;
    }

    // 3. AcquirerId eşleştir → bu cihazdaki terminalId
    const matched: string[] = [];
    for (const acquirerId of acquirerIds) {
      const terminal = deviceInfo.terminals.find(t => t.acquirerId === acquirerId);
      if (terminal) {
        matched.push(terminal.terminalId);
        console.log(`[PavoPay][SelectedTerminals] ✅ Eşleşme: acquirerId=${acquirerId} (${terminal.acquirerName}) → terminalId=${terminal.terminalId}`);
      } else {
        console.warn(`[PavoPay][SelectedTerminals] ⚠️ acquirerId=${acquirerId} bu cihazda tanımlı değil! Cihazdaki bankalar: ${deviceInfo.terminals.map(t => `${t.acquirerId}:${t.acquirerName}`).join(', ')}`);
      }
    }

    return matched.length > 0 ? matched : null;
  } catch (err) {
    console.warn('[PavoPay][SelectedTerminals] Terminal eşleştirme hatası, SelectedTerminals gönderilmeyecek:', err);
    return null;
  }
};

export interface PartialReturnPayment {
  mediator: number;
  amount: number;
  relatedPaymentId?: number;
  isVoid: boolean;
}

export const processPartialReturnForAdvanceSale = async (
  relatedSaleId: number,
  payments: PartialReturnPayment[],
  saleItems?: ReturnableSaleItem[],
  relatedSaleNumber?: string,
  relatedSaleUid?: string
): Promise<AdvanceSaleResult> => {
  // ✅ F5: Rest-P2P aktifse Pavo cihazinda avans iade
  const restCfg = getRestP2PConfig();
  if (restCfg.enabled && restCfg.deviceCode) {
    try {
      console.log('[PavoPay→RestP2P] PartialReturnForAdvance:', { relatedSaleId, paymentsCount: payments.length });
      const total = payments.reduce((s, p) => s + (p.amount || 0), 0);
      const outcome = await restP2PPaymentService.requestPartialReturnForAdvance({
        deviceCode: restCfg.deviceCode,
        relatedSaleId,
        totalRefundAmount: total,
        paymentInfos: payments.map((p) => ({
          Mediator: p.mediator,
          Amount: p.amount,
          IsVoid: p.isVoid,
          RelatedPaymentId: p.relatedPaymentId,
        })),
        saleItems: saleItems?.map((si) => ({ Id: si.id, Name: si.name, VatRate: si.vatRate })),
        companyId: restCfg.companyId,
        branchId: restCfg.branchId,
      });
      if (outcome.status !== 'completed') {
        // Cihaz "Vazgeç" durumunda errorCode='CANCELLED_BY_USER' gönderiyor.
        // (Flutter: rest_p2p_home_screen._handlePartialReturnForAdvance)
        // UI bu durumu hata yerine "iptal" mesajıyla göstersin diye
        // cancelled flag'i ayrıca set edilir.
        const isUserCancel = outcome.errorCode === 'CANCELLED_BY_USER';
        const errorMessage = isUserCancel
          ? (outcome.message || 'Kullanıcı POS ekranında iptal etti')
          : (outcome.message || 'Rest-P2P avans iadesi başarısız');
        console.warn(
          `[PavoPay/RestP2P] PartialReturnForAdvance ${isUserCancel ? 'iptal edildi' : 'başarısız'}: ${outcome.errorCode} - ${outcome.message}`,
        );
        return { success: false, cancelled: isUserCancel, errorMessage };
      }
      const r = outcome.result || {};
      return {
        success: true,
        saleNumber: r.saleNumber,
        orderNo: r.orderNo,
        invoiceNo: r.invoiceNo,
        fiscalTransactionData: r.fiscalTransactionData,
      };
    } catch (err: any) {
      return { success: false, errorMessage: err?.message || 'Rest-P2P avans iadesi hatası' };
    }
  }

  try {
    const settings = await getPavoPaySettings(true);
    if (!settings) {
      return { success: false, errorMessage: 'PavoPay ayarları bulunamadı.' };
    }

    console.log('[PavoPay] PartialReturnForAdvanceSale: Pairing ile cihaz senkronize ediliyor...');
    const pairingResult = await callPavoPayAPI('Pairing', {
      TransactionHandle: {
        SerialNumber: settings.serialNumber,
        Fingerprint: settings.fingerprint,
        TransactionSequence: 1,
        TransactionDate: new Date().toISOString()
      }
    });

    if (!pairingResult.success) {
      return { success: false, errorMessage: 'PavoPay cihazına bağlanılamadı.' };
    }

    const pairingData = pairingResult.data as any;
    const deviceSeq = pairingData?.TransactionHandle?.TransactionSequence;
    if (deviceSeq && typeof deviceSeq === 'number') {
      const syncedSeq = deviceSeq + 1;
      settings.sequence = syncedSeq;
      if (cachedSettings) cachedSettings.sequence = syncedSeq;
      updateSequence(syncedSeq).catch(() => {});
    }

    const handle = createTransactionHandle(settings);

    const paymentInformations = payments.map(p => {
      const pi: any = {
        Mediator: p.mediator,
        Amount: p.amount,
        IsVoid: p.isVoid,
      };
      if (p.isVoid && p.relatedPaymentId) {
        pi.RelatedPaymentId = p.relatedPaymentId;
      }
      return pi;
    });

    const resolvedSaleId = relatedSaleId && relatedSaleId !== 0 ? relatedSaleId : 0;
    if (!resolvedSaleId) {
      return { success: false, errorMessage: 'İlişkili satış ID bulunamadı. Lütfen GetReturnableSale ile saleId alınız.' };
    }

    const totalRefundAmount = payments.reduce((sum, p) => sum + p.amount, 0);

    const addedSaleItems = (saleItems && saleItems.length > 0)
      ? saleItems.map(item => ({
          RelatedSaleItemId: item.id,
          Name: item.name,
          ItemQuantity: 1,
          UnitPriceAmount: totalRefundAmount,
          GrossPriceAmount: totalRefundAmount,
          TotalPriceAmount: totalRefundAmount,
          ReturnAmount: totalRefundAmount,
          VATRate: item.vatRate,
        }))
      : [{
          ItemQuantity: 1,
          UnitPriceAmount: totalRefundAmount,
          GrossPriceAmount: totalRefundAmount,
          TotalPriceAmount: totalRefundAmount,
          ReturnAmount: totalRefundAmount,
          VATRate: 0,
        }];

    const saleObj: Record<string, any> = {
      RelatedSaleId: resolvedSaleId,
      TotalPriceAmount: totalRefundAmount,
      GrossPrice: totalRefundAmount,
      TotalPrice: totalRefundAmount,
      CurrencyCode: 'TRY',
      ExchangeRate: 1,
      RefererApp: 'GTS PYK',
      RefererAppVersion: '1.0.0',
      SendPhoneNotification: false,
      SendEMailNotification: false,
      AddedSaleItems: addedSaleItems,
      PaymentInformations: paymentInformations,
    };

    const saleRequest = {
      TransactionHandle: handle,
      Sale: saleObj,
    };

    console.log('[PavoPay] PartialReturnForAdvanceSale request (TotalPriceAmount + AddedSaleItems dahil):', JSON.stringify(saleRequest, null, 2));

    let result = await callPavoPayAPI('PartialReturnForAdvanceSale', saleRequest);

    if (!result.success) {
      const errData = result.data as any;
      console.error('[PavoPay] PartialReturnForAdvanceSale HATA - Full response:', JSON.stringify(errData, null, 2));

      if (errData?.ErrorCode === 24 || errData?.HasError) {
        console.log('[PavoPay] PartialReturnForAdvanceSale başarısız (ErrorCode:', errData?.ErrorCode, '), AdvanceSale tarzı minimal yapıyla tekrar deneniyor...');

        const rePairing = await callPavoPayAPI('Pairing', {
          TransactionHandle: {
            SerialNumber: settings.serialNumber,
            Fingerprint: settings.fingerprint,
            TransactionSequence: 1,
            TransactionDate: new Date().toISOString()
          }
        });
        if (rePairing.success) {
          const rpData = rePairing.data as any;
          const rpSeq = rpData?.TransactionHandle?.TransactionSequence;
          if (rpSeq && typeof rpSeq === 'number') {
            const syncSeq = rpSeq + 1;
            settings.sequence = syncSeq;
            if (cachedSettings) cachedSettings.sequence = syncSeq;
            updateSequence(syncSeq).catch(() => {});
          }
        }

        const fallbackSaleObj: Record<string, any> = {
          RelatedSaleId: resolvedSaleId,
          TotalPriceAmount: totalRefundAmount,
          CurrencyCode: 'TRY',
          ExchangeRate: 1,
          RefererApp: 'GTS PYK',
          RefererAppVersion: '1.0.0',
          SendPhoneNotification: false,
          SendEMailNotification: false,
          PaymentInformations: paymentInformations,
        };

        const fallbackRequest = {
          TransactionHandle: createTransactionHandle(settings),
          Sale: fallbackSaleObj,
        };

        console.log('[PavoPay] PartialReturnForAdvanceSale fallback (sadece TotalPriceAmount):', JSON.stringify(fallbackRequest, null, 2));

        result = await callPavoPayAPI('PartialReturnForAdvanceSale', fallbackRequest);

        if (!result.success) {
          const fallbackErrData = result.data as any;
          console.error('[PavoPay] PartialReturnForAdvanceSale fallback HATA - Full response:', JSON.stringify(fallbackErrData, null, 2));
        } else {
          console.log('[PavoPay] PartialReturnForAdvanceSale fallback BAŞARILI!');
        }
      }
    }

    const respData = result.data as any;
    const respSeq = respData?.TransactionHandle?.TransactionSequence;
    if (respSeq && typeof respSeq === 'number') {
      const nextSeq = respSeq + 1;
      if (cachedSettings) cachedSettings.sequence = nextSeq;
      updateSequence(nextSeq).catch(() => {});
    }

    if (!result.success) {
      const errMsg = respData?.Message || 'Avans iade başarısız';
      return { success: false, errorMessage: errMsg };
    }

    const saleData = respData?.Sale || respData?.Data || {};
    const saleNumber = saleData.SaleNumber || '';
    const invoiceNo = saleData.FinancialDocuments?.[0]?.InvoiceNo || '';
    const orderNo = saleData.OrderNo || '';

    const fiscalData: Record<string, any> = {
      saleNumber,
      orderNo,
      invoiceNo,
      saleId: saleData.Id,
      relatedSaleId,
      saleUid: saleData.SaleUid || '',
      statusId: saleData.StatusId,
      typeId: saleData.TypeId,
      terminalId: saleData.TerminalId,
      terminalSerialNo: saleData.TerminalSerialNo,
      returnPayments: saleData.AddedPayments?.map((p: any) => ({
        id: p.Id,
        mediatorId: p.PaymentMediatorId,
        amount: p.PaymentAmount,
        operationTypeId: p.OperationTypeId,
        relatedPaymentId: p.RelatedPaymentId,
        cardNo: p.OnlinePayment?.CardNo,
        authorizationCode: p.OnlinePayment?.AuthorizationCode,
      })),
      financialDocuments: saleData.FinancialDocuments?.map((d: any) => ({
        id: d.Id,
        invoiceNo: d.InvoiceNo,
        documentNo: d.DocumentNo,
        typeName: d.Type?.Name,
      })),
    };

    console.log('[PavoPay] PartialReturnForAdvanceSale başarılı:', { saleNumber, invoiceNo, relatedSaleId });

    return {
      success: true,
      saleNumber,
      orderNo,
      invoiceNo,
      fiscalTransactionData: JSON.stringify(fiscalData),
    };
  } catch (error: any) {
    console.error('[PavoPay] PartialReturnForAdvanceSale hata:', error);
    return { success: false, errorMessage: error.message || 'PavoPay bağlantı hatası' };
  }
};

// ============================================
// NORMAL SATIŞ KISMİ İADE (PartialReturn)
// Kulüp girişi deposito iadesi + normal satış kısmi iade için
// ============================================

export interface PartialReturnSaleItem {
  relatedSaleItemId: number;
  itemQuantity: number;
  unitPriceAmount: number;
  totalPriceAmount: number;
}

export interface PartialReturnPaymentInfo {
  mediator: number;
  amount: number;
  isVoid: boolean;
  relatedPaymentId?: number;
}

export const processPartialReturn = async (
  relatedSaleId: number,
  saleItems: PartialReturnSaleItem[],
  paymentInfos?: PartialReturnPaymentInfo[],
  orderNo?: string
): Promise<{
  success: boolean;
  errorMessage?: string;
  saleNumber?: string;
  orderNo?: string;
  invoiceNo?: string;
  fiscalTransactionData?: string;
}> => {
  // ✅ F5: Rest-P2P aktifse Pavo cihazinda kismi iade
  const restCfg = getRestP2PConfig();
  if (restCfg.enabled && restCfg.deviceCode) {
    try {
      console.log('[PavoPay→RestP2P] PartialReturn:', { relatedSaleId, items: saleItems.length });
      const outcome = await restP2PPaymentService.requestPartialReturn({
        deviceCode: restCfg.deviceCode,
        relatedSaleId,
        items: saleItems.map((it) => ({
          RelatedSaleItemId: it.relatedSaleItemId,
          ItemQuantity: it.itemQuantity,
          UnitPriceAmount: it.unitPriceAmount,
          ReturnAmount: it.totalPriceAmount,
        })),
        paymentInfos: paymentInfos?.map((pi) => ({
          Mediator: pi.mediator,
          Amount: pi.amount,
          IsVoid: pi.isVoid,
          RelatedPaymentId: pi.relatedPaymentId,
        })),
        orderNo,
        companyId: restCfg.companyId,
        branchId: restCfg.branchId,
      });
      if (outcome.status !== 'completed') {
        return { success: false, errorMessage: outcome.message || 'Rest-P2P kısmi iade başarısız' };
      }
      const r = outcome.result || {};

      // 🔎 TANI + GÜVENLİK KAPISI: Cihaz `partialReturn`'ü yalnızca ÜST SEVİYE
      //   StatusId/HasError'a bakarak "başarılı" raporlayabiliyor. Banka kart
      //   bacağını (iade ödemesi) reddettiğinde fiş belgesi oluşsa bile asıl
      //   sonuç `rawSale.AddedPayments` içindeki ÖDEME-BAZLI durumdadır. Bu yüzden
      //   ham cevabı tam logla ve iade bacağını (OperationTypeId===2) doğrula.
      try {
        console.log('[PavoPay→RestP2P] PartialReturn RAW sonuç:', JSON.stringify(r));
      } catch (_) {
        console.log('[PavoPay→RestP2P] PartialReturn RAW sonuç (stringify edilemedi):', r);
      }

      const rawSale: any = (r as any).rawSale || (r as any).RawSale || null;
      const addedPayments: any[] = Array.isArray(rawSale?.AddedPayments)
        ? rawSale.AddedPayments
        : (Array.isArray(rawSale?.addedPayments) ? rawSale.addedPayments : []);
      // İade bacağı = OperationTypeId 2 (geri ödeme/void). Orijinal ödeme bacağı
      // OperationTypeId 1'dir; onu hariç tutuyoruz.
      const refundLegs = addedPayments.filter((p) => {
        const op = p?.OperationTypeId ?? p?.operationTypeId;
        return op === 2 || op === '2';
      });
      // ⚠️ GÖZLEM MODU: İade bacağı (OperationTypeId===2) durumunu logla ama
      //   KARARI HENÜZ DEĞİŞTİRME. Yanlış bir "başarısız" kararı, gerçekte
      //   yapılmış bir iadeyi tekrar denetip ÇİFTE İADE'ye yol açabilir. Önce
      //   gerçek "banka reddi" ve gerçek "başarı" cevaplarındaki StatusId'leri
      //   bu logdan teyit edip, ardından kesin kapıyı (gate) ekleyeceğiz.
      if (refundLegs.length > 0) {
        const legInfo = refundLegs.map((p) => ({
          statusId: p?.StatusId ?? p?.statusId,
          mediator: p?.PaymentMediatorId ?? p?.paymentMediatorId,
          amount: p?.PaymentAmount ?? p?.paymentAmount,
          authCode: p?.AuthCode ?? p?.authCode ?? null,
        }));
        console.log('[PavoPay→RestP2P] 🔎 İade bacakları (OperationTypeId=2):', JSON.stringify(legInfo));
      } else {
        console.log('[PavoPay→RestP2P] 🔎 rawSale.AddedPayments içinde iade bacağı (OperationTypeId=2) bulunamadı. rawSale var mı?:', !!rawSale);
      }

      return {
        success: true,
        saleNumber: r.saleNumber,
        orderNo: r.orderNo,
        invoiceNo: r.invoiceNo,
        fiscalTransactionData: r.fiscalTransactionData,
      };
    } catch (err: any) {
      return { success: false, errorMessage: err?.message || 'Rest-P2P kısmi iade hatası' };
    }
  }

  try {
    // Pairing YAPMA: GetReturnableSale zaten Pairing yapti ve sequence'i senkronize etti.
    // Tekrar Pairing yapmak cihazin iade baglamini sifirlar.
    // cachedSettings'ten guncel sequence'i al.
    const settings = await getPavoPaySettings();
    if (!settings) {
      return { success: false, errorMessage: 'PavoPay ayarları bulunamadı.' };
    }

    console.log('[PavoPay] PartialReturn: GetReturnableSale sonrası aynı oturumda devam ediliyor. Sequence:', settings.sequence);

    const saleObj: any = {
      RefererApp: 'GTS Yazılım',
      RefererAppVersion: '1.0.0',
      RelatedSaleId: relatedSaleId,
      SendPhoneNotification: false,
      SendEMailNotification: false,
      AddedSaleItems: saleItems.map(item => ({
        RelatedSaleItemId: item.relatedSaleItemId,
        ItemQuantity: item.itemQuantity,
        UnitPriceAmount: item.unitPriceAmount,
        ReturnAmount: item.totalPriceAmount,
      })),
    };

    if (orderNo) {
      saleObj.OrderNo = orderNo;
    }

    if (paymentInfos && paymentInfos.length > 0) {
      saleObj.PaymentInformations = paymentInfos.map(pi => ({
        Mediator: pi.mediator,
        Amount: pi.amount,
        IsVoid: pi.isVoid,
        ...(pi.relatedPaymentId ? { RelatedPaymentId: pi.relatedPaymentId } : {}),
      }));
    }

    const saleRequest = {
      TransactionHandle: createTransactionHandle(settings),
      Sale: saleObj,
    };

    console.log('[PavoPay] PartialReturn request:', JSON.stringify(saleRequest, null, 2));

    const result = await callPavoPayAPI('PartialReturn', saleRequest);

    const respData = result.data as any;
    const respSeq = respData?.TransactionHandle?.TransactionSequence;
    if (respSeq && typeof respSeq === 'number') {
      const nextSeq = respSeq + 1;
      if (cachedSettings) cachedSettings.sequence = nextSeq;
      updateSequence(nextSeq).catch(() => {});
    }

    if (!result.success) {
      const errorCode = respData?.ErrorCode;
      const rawMessage = respData?.Errors?.join(', ') || respData?.Message || 'PavoPay kısmi iade başarısız';
      console.error('[PavoPay] PartialReturn HATA:', JSON.stringify(respData, null, 2));
      let errorMessage = rawMessage;
      if (errorCode === 161) {
        errorMessage = 'POS cihazında "İade Bilgi Fişi" uygulaması bulunamadı. PavoPay destek ile iletişime geçerek iade modülünün cihazınızda aktifleştirilmesini talep edin.';
      } else if (errorCode === 170) {
        errorMessage = 'E-Faturalı satış için kısmi iade yapılamaz. Lütfen PavoPay destek ile iletişime geçin.';
      }
      return {
        success: false,
        errorMessage,
      };
    }

    const responseData = respData;
    const saleNumber = responseData?.Data?.SaleNumber || responseData?.SaleNumber;
    const invoiceNo = responseData?.Data?.InvoiceNo || responseData?.InvoiceNo;

    const fiscalData = {
      type: 'PAVOPAY_PARTIAL_RETURN',
      relatedSaleId,
      saleNumber,
      invoiceNo,
      timestamp: new Date().toISOString(),
      saleItems: saleItems.map(si => ({
        saleItemId: si.relatedSaleItemId,
        amount: si.totalPriceAmount,
      })),
    };

    console.log('[PavoPay] PartialReturn başarılı:', { saleNumber, invoiceNo, relatedSaleId });

    return {
      success: true,
      saleNumber,
      orderNo,
      invoiceNo,
      fiscalTransactionData: JSON.stringify(fiscalData),
    };
  } catch (error: any) {
    console.error('[PavoPay] PartialReturn hata:', error);
    return { success: false, errorMessage: error.message || 'PavoPay bağlantı hatası' };
  }
};

// ✅ PavoPay Müşteri Bilgisi Sorgulama (QueryCustomerInfo)
export const queryCustomerInfo = async (
  tckn?: string,
  vkn?: string
): Promise<PavoPayCustomerQueryResult> => {
  try {
    const settings = await getPavoPaySettings(true);
    if (!settings) {
      return { success: false, errorMessage: 'PavoPay ayarları bulunamadı' };
    }

    // Pairing ile cihaz senkronize et
    console.log('[PavoPay] QueryCustomerInfo: Pairing ile cihaz senkronize ediliyor...');
    const pairingResult = await callPavoPayAPI('Pairing', {
      TransactionHandle: {
        SerialNumber: settings.serialNumber,
        Fingerprint: settings.fingerprint,
        TransactionSequence: 1,
        TransactionDate: new Date().toISOString()
      }
    });

    if (pairingResult.success) {
      const pairingData = pairingResult.data as any;
      const deviceSeq = pairingData?.TransactionHandle?.TransactionSequence;
      if (deviceSeq && typeof deviceSeq === 'number') {
        const syncedSeq = deviceSeq + 1;
        settings.sequence = syncedSeq;
        if (cachedSettings) cachedSettings.sequence = syncedSeq;
        updateSequence(syncedSeq).catch(() => {});
        console.log(`[PavoPay] QueryCustomerInfo: Sequence senkronize edildi: device=${deviceSeq}, next=${syncedSeq}`);
      }
    }

    const handle = createTransactionHandle(settings);
    const customerInput = tckn ? { Tckn: tckn } : vkn ? { Vkn: vkn } : {};
    console.log('[PavoPay] QueryCustomerInfo isteği gönderiliyor...', { tckn, vkn, customerInput, sequence: handle.TransactionSequence });

    const saleRequest: any = {
      TransactionHandle: handle,
      CustomerInput: customerInput,
    };

    const apiResult = await callPavoPayAPI('QueryCustomerInfo', saleRequest);

    if (!apiResult.success) {
      const errMsg = (apiResult.data as any)?.Message || 'Sorgu başarısız';
      console.error('[PavoPay] QueryCustomerInfo başarısız:', errMsg);
      return { success: false, errorMessage: errMsg };
    }

    const responseData = apiResult.data as any;
    const respHandle = responseData?.TransactionHandle;
    if (respHandle?.TransactionSequence && typeof respHandle.TransactionSequence === 'number') {
      const newSeq = respHandle.TransactionSequence + 1;
      settings.sequence = newSeq;
      if (cachedSettings) cachedSettings.sequence = newSeq;
      updateSequence(newSeq).catch(() => {});
      console.log(`[PavoPay] QueryCustomerInfo: Response sequence güncellendi: device=${respHandle.TransactionSequence}, next=${newSeq}`);
    }

    const customerInfo = responseData?.Data?.CustomerInfo;
    if (!customerInfo) {
      return { success: false, errorMessage: 'Müşteri bilgisi bulunamadı' };
    }

    const hasCompanyName = !!customerInfo.unvan && customerInfo.unvan.trim() !== '';
    const hasVkn = !!customerInfo.vkn && customerInfo.vkn.trim() !== '';
    const isCompany = hasCompanyName && hasVkn;

    const customerParty: PavoPayCustomerParty = {
      CustomerType: isCompany ? 2 : 1,
      CompanyName: isCompany ? customerInfo.unvan || customerInfo.kimlikUnvani : undefined,
      FirstName: customerInfo.ad || '',
      FamilyName: customerInfo.soyad || '',
      TaxNumber: isCompany ? (customerInfo.vkn || customerInfo.tckn || '') : (customerInfo.tckn || customerInfo.vkn || ''),
      TaxOfficeName: customerInfo.vergiDairesiAdi || '',
      TaxOfficeCode: customerInfo.vkn === '9220036550' ? '001103' : (customerInfo.vergiDairesiKodu || ''),
      Country: 'Türkiye',
      City: '',
      District: '',
      Address: '',
    };

    if (customerInfo.adresBilgileri && customerInfo.adresBilgileri.length > 0) {
      const addr = customerInfo.adresBilgileri[0];
      customerParty.City = addr.ilAdi || '';
      customerParty.District = addr.ilceAdi || '';
      const parts = [
        addr.mahalleSemt, addr.caddeSokak,
        addr.disKapiNo ? `No:${addr.disKapiNo}` : '',
        addr.ilceAdi ? `${addr.ilceAdi}/${addr.ilAdi}` : addr.ilAdi,
      ].filter(Boolean);
      customerParty.Address = parts.join(' ');
    }

    const displayName = isCompany
      ? (customerInfo.unvan || customerInfo.kimlikUnvani || '')
      : `${customerInfo.ad || ''} ${customerInfo.soyad || ''}`.trim();

    console.log('[PavoPay] QueryCustomerInfo başarılı:', {
      name: displayName,
      isEInvoice: isCompany,
      isEArchive: !isCompany,
      taxOffice: customerInfo.vergiDairesiAdi,
    });

    return {
      success: true,
      customerParty,
      rawInfo: customerInfo,
    };
  } catch (error: any) {
    console.error('[PavoPay] QueryCustomerInfo hata:', error);
    return { success: false, errorMessage: error.message || 'Bağlantı hatası' };
  }
};
